import * as THREE from 'three';
import {
  DEFAULT_WIND_LAYER_BLEND_MODE,
  VISUAL_SETTINGS,
  WIND_LEGEND_SCALE,
  decodeWindVector,
  formatWindLayerBlendButtonLabel,
  formatWindLayerBlendLabel,
  getWindLayerBlendCount,
  getWindColor,
  getNextWindLayerBlendMode,
  getWindMagnitudeRatio,
  hexToRgb,
  normaliseWindMagnitudeRange,
} from '../../constants.js';
import { sampleWindAtWorldPoint } from '../onClickHandler.js';

const XR_REFERENCE_SPACE = 'local';
const XR_SESSION_OPTIONS = Object.freeze({
  optionalFeatures: ['local-floor', 'bounded-floor', 'layers'],
});
const XR_UP = new THREE.Vector3(0, 1, 0);
const XR_DEFAULT_FORWARD = new THREE.Vector3(0, 0, -1);
const XR_PLANE_NORMAL = new THREE.Vector3(0, 0, 1);
const XR_PLANET_DISTANCE = 2.25;
const XR_PLANET_MIN_DISTANCE = 1.35;
const XR_PLANET_MAX_DISTANCE = 3.6;
const XR_HUD_PLANET_FORWARD_OFFSET = -0.34;
const XR_HUD_SIDE_OFFSET = 2.18;
const XR_HUD_VERTICAL_OFFSET = -0.13;
const XR_HUD_SCALE = 1;
const XR_HUD_PANEL_WIDTH = 2.78;
const XR_HUD_PANEL_HEIGHT = 0.78;
const XR_HUD_LEGEND_WIDTH = 2.78;
const XR_HUD_LEGEND_HEIGHT = 0.42;
const XR_HUD_PANEL_Y = 0.36;
const XR_HUD_LEGEND_Y = -0.82;
const XR_HUD_BUTTON_HEIGHT = 0.13;
const XR_HUD_BACKPLATE_WIDTH = 2.94;
const XR_HUD_BACKPLATE_HEIGHT = 2.08;
const XR_PLANET_SIDE_OFFSET = 0;
const XR_PLANET_VERTICAL_OFFSET = -0.05;
const XR_RECENTER_FRAME_COUNT = 24;
const XR_PLANET_COLOR = 0x243141;
const XR_PLANET_DARK_GRAY_COLOR = 0x30343a;
const XR_ATMOSPHERE_RADIUS = 1.075;
const XR_FLOW_DIAGNOSTIC_RADIUS = 1.023;
const XR_FLOW_DIAGNOSTIC_OPACITY = 0.54;
const XR_HEAD_MARKER_OFFSET = new THREE.Vector3(0, -0.34, -1.24);
const XR_CONTROLLER_RAY_LENGTH = 4;
const XR_SELECT_DEBOUNCE_MS = 180;
const XR_STATION_TELEMETRY_INTERVAL_MS = 700;
const XR_STATION_LOW_FPS_THRESHOLD = 45;
const XR_STATION_LOW_FPS_GRACE_MS = 2600;
const XR_STATION_RECOVERY_FPS_THRESHOLD = 58;
const XR_STATION_RECOVERY_GRACE_MS = 5200;
const XR_WIND_TRACER_QUALITY_PRESETS = Object.freeze({
  performance: Object.freeze({ label: 'performance', count: 2048, trailSegments: 22 }),
  balanced: Object.freeze({ label: 'balanced', count: 4096, trailSegments: 28 }),
  high: Object.freeze({ label: 'high', count: 6144, trailSegments: 32 }),
});
const XR_WIND_TRACER_QUALITY_ORDER = Object.freeze(['performance', 'balanced', 'high']);
const XR_INITIAL_WIND_TRACER_QUALITY = getWindTracerQualityPreset();
const XR_WIND_TRACER_RADIUS = 1.035;
const XR_WIND_LAYER_ALTITUDE_STEP = 0.018;
const XR_WIND_TRACER_WIDTH = 0.0066;
const XR_WIND_TRACER_OPACITY = 0.95;
const XR_WIND_TRACER_MOTION_SCALE = VISUAL_SETTINGS.motionScale * 2.85;
const XR_WIND_TRACER_MIN_ARC_STEP = 0.0048;
const XR_WIND_TRACER_MAX_ARC_STEP = 0.064;
const XR_WIND_TRACER_MAX_ACCUMULATED_DELTA = 0.07;
const XR_WIND_TRACER_RESPAWN_CHANCE = 0.00022;
const XR_WIND_TRACER_MIN_LIFETIME = 260;
const XR_WIND_TRACER_MAX_LIFETIME = 620;
const XR_WIND_TRACER_FADE_FRAMES = 58;
const XR_WIND_TRACER_AGE_RATE = 20;
const XR_WIND_TRACER_TAIL_FADE_EXPONENT = 1.42;
const XR_WIND_TRACER_TAIL_MIN_FADE = 0.015;
const XR_WIND_TRACER_SEED_CANDIDATES = 4;
const XR_WIND_TRACER_GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));
const XR_WIND_TRACER_MIN_MOTION_RATIO = 0.055;
const XR_SYNTHETIC_WIND_MAX_MAGNITUDE = 150;
const XR_SURFACE_STYLES = Object.freeze({
  darkGray: 'dark-gray',
  data: 'data',
});
const XR_STAR_COUNT = 1400;
const XR_STAR_INNER_RADIUS = 12;
const XR_STAR_OUTER_RADIUS = 28;
const XR_ROTATION_DEADZONE = 0.16;
const XR_THUMBSTICK_MOVE_SPEED = 0.78;
const XR_THUMBSTICK_ZOOM_SPEED = 0.98;
const XR_R1_ZOOM_DISTANCE_SCALE = 1.65;
const XR_START_TIMEOUT_MS = 30000;
const XR_REQUEST_TIMEOUT_MESSAGE =
  'VR did not start. Open this app in a standalone Meta Quest Browser tab and try again.';
const XR_SETUP_TIMEOUT_MESSAGE =
  'VR setup timed out while preparing the headset view. Refresh the page and try Enter VR again.';
const streamUnit = new THREE.Vector3();
const streamDirection = new THREE.Vector3();
const streamEast = new THREE.Vector3();
const streamNorth = new THREE.Vector3();
const tracerCurrent = new THREE.Vector3();
const tracerMid = new THREE.Vector3();
const tracerNext = new THREE.Vector3();
const tracerCenterA = new THREE.Vector3();
const tracerCenterB = new THREE.Vector3();
const tracerTangent = new THREE.Vector3();
const tracerSideA = new THREE.Vector3();
const tracerSideB = new THREE.Vector3();
const tracerLeft = new THREE.Vector3();
const tracerSeedCandidate = new THREE.Vector3();
const tracerWindA = { lon: 0, lat: 0, magnitude: 0 };
const tracerWindB = { lon: 0, lat: 0, magnitude: 0 };
const gripZoomControllerPosition = new THREE.Vector3();
const windTracerColorScale = WIND_LEGEND_SCALE.map((stop) => ({
  ...stop,
  rgb: hexToRgb(stop.color),
}));

export function createWebXRExperience({
  camera,
  renderer,
  controls,
  worldGroup,
  getPlanetMesh,
  getVelocityTexture,
  setPixelRatioCap,
  onPointSelected,
  onPresentingChange,
  onTextureLevelStep,
  onSurfaceModeChange,
  onShowWindsChange,
  onXrSurfaceOpaqueChange,
  onWindLayerBlendModeChange,
  onStatusChange,
}) {
  renderer.xr.enabled = true;
  renderer.xr.setReferenceSpaceType(XR_REFERENCE_SPACE);
  const reportStatus = reportStatusFactory(onStatusChange);

  const xrScene = new THREE.Scene();
  xrScene.background = new THREE.Color(0x01050d);

  const state = {
    textureLevel: null,
    surfaceMode: null,
    showWinds: true,
    windMagnitudeLimit: null,
    windMagnitudeRange: null,
    legendEntries: [],
    selectedPoint: null,
    stationTelemetry: createInitialStationTelemetry(),
    surfaceOpaque: true,
    windLayerBlendMode: DEFAULT_WIND_LAYER_BLEND_MODE,
    xrSurfaceStyle: XR_SURFACE_STYLES.darkGray,
    windTracerQuality: XR_INITIAL_WIND_TRACER_QUALITY.label,
    hudSideOffset: XR_HUD_SIDE_OFFSET,
    hudVerticalOffset: XR_HUD_VERTICAL_OFFSET,
    hudForwardOffset: XR_HUD_PLANET_FORWARD_OFFSET,
    planetSideOffset: XR_PLANET_SIDE_OFFSET,
    planetVerticalOffset: XR_PLANET_VERTICAL_OFFSET,
    planetDistance: XR_PLANET_DISTANCE,
    planetScale: VISUAL_SETTINGS.webxrQuest.defaultPlanetScale,
  };
  const raycaster = new THREE.Raycaster();
  const tempMatrix = new THREE.Matrix4();
  const inputSourceMatrix = new THREE.Matrix4();
  const viewerPosition = new THREE.Vector3();
  const viewerForward = new THREE.Vector3();
  const viewerRight = new THREE.Vector3();
  const viewerQuaternion = new THREE.Quaternion();
  const gripDragPlane = new THREE.Plane();
  const gripDragPoint = new THREE.Vector3();
  const gripDragDelta = new THREE.Vector3();
  const hudNormal = new THREE.Vector3();
  const hudQuaternion = new THREE.Quaternion();
  const xrRoot = new THREE.Group();
  const xrPlanetPivot = new THREE.Group();
  const xrPlanetMesh = createXrPlanetMesh();
  const xrAtmosphereShell = createXrAtmosphereShell();
  const xrFlowDiagnosticShell = createXrFlowDiagnosticShell();
  const xrFallbackPlanetTexture = xrPlanetMesh.material.map;
  const xrWindTrailGroup = new THREE.Group();
  xrWindTrailGroup.name = 'xr-wind-altitude-stack';
  let xrWindTrailLayers = createXrWindTrailLayerEntries(1, XR_INITIAL_WIND_TRACER_QUALITY);
  xrWindTrailLayers.forEach((layer) => xrWindTrailGroup.add(layer.mesh));
  const xrStars = createXrStarField();
  const xrHeadLockedMarker = createHeadLockedMarker();
  const hud = createHud({
    onAction: (action) => handleAction(action),
  });
  const controllers = [0, 1].map((index) => createController(renderer, index));
  const grabPreviousPoint = new THREE.Vector3();
  const grabCurrentPoint = new THREE.Vector3();
  const grabDeltaQuaternion = new THREE.Quaternion();
  let savedDesktopState = null;
  let pendingViewerRecenterFrames = 0;
  let xrSession = null;
  let lastSelectAt = 0;
  let activeGrabController = null;
  let activeGripDrag = null;
  let hasReceivedXrFrame = false;
  let velocitySurfaceSource = null;
  let velocitySurfaceRangeKey = '';
  let velocitySurfaceTexture = null;
  let velocityDiagnosticSource = null;
  let velocityDiagnosticRangeKey = '';
  let velocityDiagnosticTexture = null;
  let velocityLayerSamplerSources = [];
  let velocityLayerSamplers = [];
  let windTracerAccumulatedDelta = 0;
  let lastFrameStepError = '';
  let lastFrameStepErrorAt = 0;
  let stationTelemetrySampleStartedAt = 0;
  let stationTelemetryLastFrameAt = 0;
  let stationTelemetryFrameCount = 0;
  let stationTelemetryLowFpsStartedAt = 0;
  let stationTelemetryRecoveryStartedAt = 0;
  let stationTelemetryAutoFlowFallback = false;
  let windTracerUserQualityLabel = XR_INITIAL_WIND_TRACER_QUALITY.label;

  xrPlanetPivot.add(xrPlanetMesh);
  xrPlanetPivot.add(xrAtmosphereShell);
  xrPlanetPivot.add(xrFlowDiagnosticShell);
  xrPlanetPivot.add(xrWindTrailGroup);
  xrRoot.add(xrPlanetPivot);
  xrScene.add(xrStars);
  xrScene.add(xrRoot);
  xrHeadLockedMarker.visible = false;
  xrScene.add(xrHeadLockedMarker);
  hud.group.visible = false;
  xrScene.add(hud.group);
  controllers.forEach((controller) => {
    controller.visible = false;
    controller.addEventListener('connected', (event) => {
      cacheControllerInputSource(controller, event?.data);
      controller.visible = renderer.xr.isPresenting;
    });
    controller.addEventListener('disconnected', (event) => {
      controller.visible = false;
      controller.userData.xrButtonState = [];
      clearControllerInputSource(controller, event?.data);
    });
    controller.addEventListener('select', (event) => {
      cacheControllerInputSource(controller, event?.data);
      requestControllerSelect(controller);
    });
    controller.addEventListener('selectstart', (event) => {
      cacheControllerInputSource(controller, event?.data);
      requestControllerSelect(controller);
    });
    controller.addEventListener('selectend', (event) => {
      cacheControllerInputSource(controller, event?.data);
      stopPlanetGrab(controller);
    });
    controller.addEventListener('squeezestart', (event) => {
      cacheControllerInputSource(controller, event?.data);
      startGripDrag(controller);
    });
    controller.addEventListener('squeezeend', (event) => {
      cacheControllerInputSource(controller, event?.data);
      stopGripDrag(controller);
    });
    xrScene.add(controller);
  });

  function updateState(nextState) {
    Object.assign(state, nextState);
    hud.update(state);
  }

  function updateSpatialState(nextState) {
    Object.assign(state, nextState);
  }

  async function enterVR() {
    if (!navigator?.xr || typeof navigator.xr.requestSession !== 'function') {
      reportStatus('WebXR requestSession is unavailable');
      throw new Error('WebXR immersive VR is not available in this browser.');
    }

    if (!window.isSecureContext) {
      reportStatus('Blocked: page is not a secure context');
      throw new Error('WebXR requires this page to be served over HTTPS.');
    }

    let session = null;

    try {
      const requestSessionPromise = navigator.xr.requestSession('immersive-vr', XR_SESSION_OPTIONS);
      reportStatus('Requesting standard immersive-vr session');
      session = await withTimeout(
        requestSessionPromise,
        XR_START_TIMEOUT_MS,
        XR_REQUEST_TIMEOUT_MESSAGE,
        {
          onTimeout: () => reportStatus('requestSession timed out'),
          onLateResolve: endXrSessionQuietly,
        }
      );

      reportStatus('Session granted; configuring renderer');
      await withTimeout(
        renderer.xr.setSession(session),
        XR_START_TIMEOUT_MS,
        XR_SETUP_TIMEOUT_MESSAGE,
        {
          onTimeout: () => {
            reportStatus('Renderer setup timed out');
            endXrSessionQuietly(session);
          },
        }
      );
      reportStatus('Renderer session configured');
    } catch (error) {
      reportStatus(`Error: ${formatXrError(error)}`);

      if (session && !renderer.xr.isPresenting) {
        endXrSessionQuietly(session);
      }

      throw error;
    }
  }

  function onSessionStart() {
    xrSession = renderer.xr.getSession();
    hasReceivedXrFrame = false;
    reportStatus('Session started; waiting for headset frame');
    xrSession?.addEventListener('select', requestSessionSelect);
    xrSession?.addEventListener('selectstart', requestSessionSelect);
    xrSession?.addEventListener('selectend', requestSessionSelectEnd);
    xrSession?.addEventListener('squeezestart', requestSessionGripStart);
    xrSession?.addEventListener('squeezeend', requestSessionGripEnd);

    savedDesktopState = {
      worldPosition: worldGroup.position.clone(),
      worldQuaternion: worldGroup.quaternion.clone(),
      worldScale: worldGroup.scale.clone(),
      cameraPosition: camera.position.clone(),
      cameraQuaternion: camera.quaternion.clone(),
      controlsEnabled: controls.enabled,
      controlsTarget: controls.target.clone(),
    };

    controls.enabled = false;
    camera.position.set(0, 0, 0);
    camera.quaternion.identity();
    setPixelRatioCap?.(VISUAL_SETTINGS.webxrQuest.maxPixelRatio);
    resetStationTelemetry();
    resetView();
    pendingViewerRecenterFrames = XR_RECENTER_FRAME_COUNT;

    xrRoot.visible = true;
    xrHeadLockedMarker.visible = false;
    hud.group.visible = true;
    controllers.forEach((controller) => {
      controller.visible = true;
    });
    onPresentingChange?.(true);
  }

  function onSessionEnd() {
    xrSession?.removeEventListener('select', requestSessionSelect);
    xrSession?.removeEventListener('selectstart', requestSessionSelect);
    xrSession?.removeEventListener('selectend', requestSessionSelectEnd);
    xrSession?.removeEventListener('squeezestart', requestSessionGripStart);
    xrSession?.removeEventListener('squeezeend', requestSessionGripEnd);
    xrSession = null;

    xrRoot.visible = false;
    xrHeadLockedMarker.visible = false;
    hud.group.visible = false;
    pendingViewerRecenterFrames = 0;
    activeGrabController = null;
    activeGripDrag = null;
    controllers.forEach((controller) => {
      controller.visible = false;
    });

    if (savedDesktopState) {
      worldGroup.position.copy(savedDesktopState.worldPosition);
      worldGroup.quaternion.copy(savedDesktopState.worldQuaternion);
      worldGroup.scale.copy(savedDesktopState.worldScale);
      camera.position.copy(savedDesktopState.cameraPosition);
      camera.quaternion.copy(savedDesktopState.cameraQuaternion);
      controls.target.copy(savedDesktopState.controlsTarget);
      controls.enabled = savedDesktopState.controlsEnabled;
      controls.update();
      savedDesktopState = null;
    }

    setPixelRatioCap?.(VISUAL_SETTINGS.maxPixelRatio);
    resetStationTelemetry();

    reportStatus('Session ended');
    onPresentingChange?.(false);
  }

  function resetView(xrFrame) {
    readViewerPose(xrFrame);
    resetViewFromCurrentPose();
    updateHeadLockedMarker();
  }

  function setWindTracerQuality(quality, { automatic = false } = {}) {
    const nextQuality = getWindTracerQualityPresetByLabel(quality);
    const wasAutomaticFallback = stationTelemetryAutoFlowFallback;

    if (!automatic) {
      windTracerUserQualityLabel = nextQuality.label;
    }

    stationTelemetryAutoFlowFallback = nextQuality.label !== windTracerUserQualityLabel;
    stationTelemetryLowFpsStartedAt = 0;
    stationTelemetryRecoveryStartedAt = 0;

    if (nextQuality.label !== state.windTracerQuality) {
      xrWindTrailLayers.forEach((layer) => {
        applyWindTracerQuality(
          layer.mesh,
          getWindTracerLayerQuality(nextQuality, xrWindTrailLayers.length)
        );
        layer.qualityKey = '';
      });
    }

    updateState({
      windTracerQuality: nextQuality.label,
      stationTelemetry: {
        ...state.stationTelemetry,
        autoFlowFallback: stationTelemetryAutoFlowFallback,
      },
    });

    if (nextQuality.label === state.windTracerQuality && wasAutomaticFallback === stationTelemetryAutoFlowFallback) {
      return;
    }

    reportStatus(
      automatic
        ? `Adaptive tracer density: ${formatWindTracerQualityLabel(nextQuality.label)}`
        : `Tracer density: ${formatWindTracerQualityLabel(nextQuality.label)}`
    );
  }

  function cycleWindTracerQuality() {
    setWindTracerQuality(getNextWindTracerQualityPreset(state.windTracerQuality).label);
  }

  function handleAction(action) {
    if (action === 'reset') {
      pendingViewerRecenterFrames = XR_RECENTER_FRAME_COUNT;
      return;
    }

    if (action === 'exit') {
      renderer.xr.getSession()?.end();
      return;
    }

    if (action === 'layer-down') {
      onTextureLevelStep?.(-1);
      return;
    }

    if (action === 'layer-up') {
      onTextureLevelStep?.(1);
      return;
    }

    if (action === 'surface-constant') {
      updateState({ surfaceMode: 'constant' });
      onSurfaceModeChange?.('constant');
      return;
    }

    if (action === 'surface-dark') {
      updateState({ xrSurfaceStyle: XR_SURFACE_STYLES.darkGray });
      return;
    }

    if (action === 'surface-data') {
      updateState({
        xrSurfaceStyle: XR_SURFACE_STYLES.data,
        surfaceMode: 'constant',
      });
      onSurfaceModeChange?.('constant');
      return;
    }

    if (action === 'surface-pressure') {
      updateState({
        xrSurfaceStyle: XR_SURFACE_STYLES.data,
        surfaceMode: 'pressure',
      });
      onSurfaceModeChange?.('pressure');
      return;
    }

    if (action === 'winds-on') {
      updateState({ showWinds: true });
      onShowWindsChange?.(true);
      return;
    }

    if (action === 'winds-off') {
      updateState({ showWinds: false });
      onShowWindsChange?.(false);
      return;
    }

    if (action === 'wind-quality') {
      cycleWindTracerQuality();
      return;
    }

    if (action === 'layer-blend') {
      const nextMode = getNextWindLayerBlendMode(state.windLayerBlendMode);
      updateState({ windLayerBlendMode: nextMode });
      onWindLayerBlendModeChange?.(nextMode);
      reportStatus(`Layer combination: ${formatWindLayerBlendLabel(nextMode)}`);
      return;
    }

    if (action === 'opaque-on') {
      updateState({ surfaceOpaque: true });
      onXrSurfaceOpaqueChange?.(true);
      return;
    }

    if (action === 'opaque-off') {
      updateState({ surfaceOpaque: false });
      onXrSurfaceOpaqueChange?.(false);
    }
  }

  function requestControllerSelect(controller) {
    if (!renderer.xr.isPresenting) {
      return;
    }

    if (!canSelectNow()) {
      return;
    }

    selectWithController(controller);
  }

  function requestSessionSelect(event) {
    if (!renderer.xr.isPresenting || !canSelectNow()) {
      return;
    }

    const controller = findControllerForInputSource(controllers, event?.inputSource) ?? controllers[0];
    cacheControllerInputSource(controller, event?.inputSource);

    if (setRayFromInputSourceEvent(raycaster, event, renderer, inputSourceMatrix, tempMatrix)) {
      selectWithRay(raycaster, controller);
      return;
    }

    selectWithController(controller);
  }

  function requestSessionSelectEnd(event) {
    const controller = findControllerForInputSource(controllers, event?.inputSource);
    cacheControllerInputSource(controller, event?.inputSource);
    stopPlanetGrab(controller);
  }

  function requestSessionGripStart(event) {
    if (!renderer.xr.isPresenting) {
      return;
    }

    const controller = findControllerForInputSource(controllers, event?.inputSource) ?? controllers[0];
    cacheControllerInputSource(controller, event?.inputSource);
    startGripDrag(controller);
  }

  function requestSessionGripEnd(event) {
    const controller = findControllerForInputSource(controllers, event?.inputSource);
    cacheControllerInputSource(controller, event?.inputSource);
    stopGripDrag(controller);
  }

  function canSelectNow() {
    const now = performance.now();

    if ((now - lastSelectAt) < XR_SELECT_DEBOUNCE_MS) {
      return false;
    }

    lastSelectAt = now;
    return true;
  }

  function selectWithController(controller) {
    setRayFromController(raycaster, controller, tempMatrix);
    selectWithRay(raycaster, controller);
  }

  function selectWithRay(activeRaycaster, controller = null) {
    hud.group.updateMatrixWorld(true);
    xrRoot.updateMatrixWorld(true);

    const uiHits = activeRaycaster.intersectObjects(hud.interactiveObjects, false);

    if (uiHits.length) {
      handleAction(uiHits[0].object.userData.xrAction);
      return;
    }

    const planetMesh = xrPlanetMesh;
    const velocityTexture = getVelocityTexture?.();
    if (!planetMesh || !velocityTexture?.image) {
      return;
    }

    const planetHits = activeRaycaster.intersectObject(planetMesh, false);
    if (!planetHits.length) {
      return;
    }

    if (controller) {
      startPlanetGrab(controller, planetHits[0].point);
    }

    const payload = sampleWindAtWorldPoint({
      point: planetHits[0].point,
      planetMesh,
      velocityTexture,
      windMagnitudeRange: getActiveWindMagnitudeRange(state),
      windMagnitudeLimit: state.windMagnitudeLimit,
    });

    if (payload) {
      updateState({ selectedPoint: payload });
      onPointSelected?.(payload);
    }
  }

  function beforeFrame(delta, xrFrame) {
    if (!renderer.xr.isPresenting) {
      return;
    }

    if (!hasReceivedXrFrame) {
      hasReceivedXrFrame = true;
      reportStatus('Headset frame received');
    }

    runXrFrameStep('read viewer pose', () => readViewerPose(xrFrame));
    runXrFrameStep('update station telemetry', updateStationTelemetry);
    runXrFrameStep('update stars', updateXrStars);
    runXrFrameStep('update head marker', updateHeadLockedMarker);

    if (pendingViewerRecenterFrames > 0) {
      pendingViewerRecenterFrames -= 1;
      runXrFrameStep('reset view', resetViewFromCurrentPose);
    }

    runXrFrameStep('sync planet material', syncXrPlanetMaterial);
    runXrFrameStep('sync flow diagnostic', syncXrFlowDiagnosticShell);
    runXrFrameStep('update wind tracers', () => updateXrWindTracers(delta));
    runXrFrameStep('update hud hover', updateHudHover);

    controllers.forEach((controller, index) => {
      runXrFrameStep(`controller ${index}`, () => {
        pollControllerButtons(
          controller,
          () => stopPlanetGrab(controller),
          () => startGripDrag(controller),
          () => stopGripDrag(controller)
        );
        updateGripDrag(controller);
        updatePlanetGrab(controller);
        applyThumbstickNavigation(
          controller,
          delta,
          state,
          updateSpatialState,
          updatePlanetFromCurrentPose
        );
      });
    });
  }

  function updateHudHover() {
    if (!hud.group.visible) {
      hud.setPointerTarget(null, null);
      return;
    }

    hud.group.updateMatrixWorld(true);

    let closestHit = null;

    controllers.forEach((controller) => {
      if (!controller.visible) {
        return;
      }

      setRayFromController(raycaster, controller, tempMatrix);
      const hits = raycaster.intersectObjects(hud.interactiveObjects, false);

      if (!hits.length) {
        return;
      }

      if (!closestHit || hits[0].distance < closestHit.distance) {
        closestHit = hits[0];
      }
    });

    hud.setPointerTarget(
      closestHit?.object?.userData?.xrAction ?? null,
      closestHit?.point ?? null
    );
  }

  function runXrFrameStep(label, action) {
    try {
      action();
    } catch (error) {
      reportXrFrameStepError(label, error);
    }
  }

  function reportXrFrameStepError(label, error) {
    const message = `${label}: ${formatXrError(error)}`;
    const now = performance.now();

    if (message !== lastFrameStepError || (now - lastFrameStepErrorAt) > 1500) {
      lastFrameStepError = message;
      lastFrameStepErrorAt = now;
      reportStatus(`XR step skipped: ${message}`);
    }

    if (typeof window !== 'undefined') {
      window.__OASIS_XR_FRAME_STEP_ERROR__ = {
        label,
        message: formatXrError(error),
        stack: error?.stack ?? null,
        at: new Date().toISOString(),
      };
    }
  }

  function resetStationTelemetry() {
    stationTelemetrySampleStartedAt = 0;
    stationTelemetryLastFrameAt = 0;
    stationTelemetryFrameCount = 0;
    stationTelemetryLowFpsStartedAt = 0;
    stationTelemetryRecoveryStartedAt = 0;
    stationTelemetryAutoFlowFallback = false;
    updateState({ stationTelemetry: createInitialStationTelemetry() });
  }

  function updateStationTelemetry() {
    const now = performance.now();

    if (!stationTelemetryLastFrameAt) {
      stationTelemetryLastFrameAt = now;
      stationTelemetrySampleStartedAt = now;
      stationTelemetryFrameCount = 0;
      return;
    }

    const frameMs = now - stationTelemetryLastFrameAt;
    stationTelemetryLastFrameAt = now;
    stationTelemetryFrameCount += 1;

    const elapsedMs = now - stationTelemetrySampleStartedAt;
    if (elapsedMs < XR_STATION_TELEMETRY_INTERVAL_MS) {
      return;
    }

    const fps = (stationTelemetryFrameCount * 1000) / Math.max(elapsedMs, 1);
    handleStationPerformanceFallback(fps, now);
    updateState({
      stationTelemetry: {
        fps: Math.round(fps),
        frameMs: Math.round(frameMs * 10) / 10,
        status: getStationPerformanceStatus(fps),
        autoFlowFallback: stationTelemetryAutoFlowFallback,
      },
    });

    stationTelemetrySampleStartedAt = now;
    stationTelemetryFrameCount = 0;
  }

  function handleStationPerformanceFallback(fps, now) {
    const currentIndex = getWindTracerQualityOrderIndex(state.windTracerQuality);
    const targetIndex = getWindTracerQualityOrderIndex(windTracerUserQualityLabel);

    if (fps < XR_STATION_LOW_FPS_THRESHOLD) {
      stationTelemetryRecoveryStartedAt = 0;

      if (currentIndex <= 0) {
        stationTelemetryLowFpsStartedAt = 0;
        return;
      }

      if (!stationTelemetryLowFpsStartedAt) {
        stationTelemetryLowFpsStartedAt = now;
        return;
      }

      if ((now - stationTelemetryLowFpsStartedAt) < XR_STATION_LOW_FPS_GRACE_MS) {
        return;
      }

      setWindTracerQuality(XR_WIND_TRACER_QUALITY_ORDER[currentIndex - 1], { automatic: true });
      return;
    }

    stationTelemetryLowFpsStartedAt = 0;

    if (
      fps < XR_STATION_RECOVERY_FPS_THRESHOLD ||
      currentIndex >= targetIndex ||
      !stationTelemetryAutoFlowFallback
    ) {
      stationTelemetryRecoveryStartedAt = 0;
      return;
    }

    if (!stationTelemetryRecoveryStartedAt) {
      stationTelemetryRecoveryStartedAt = now;
      return;
    }

    if ((now - stationTelemetryRecoveryStartedAt) < XR_STATION_RECOVERY_GRACE_MS) {
      return;
    }

    setWindTracerQuality(XR_WIND_TRACER_QUALITY_ORDER[currentIndex + 1], { automatic: true });
  }

  function readViewerPose(xrFrame) {
    const referenceSpace = renderer.xr.getReferenceSpace?.();
    const viewerPose = xrFrame && typeof xrFrame.getViewerPose === 'function' && referenceSpace
      ? xrFrame.getViewerPose(referenceSpace)
      : null;

    if (viewerPose?.transform) {
      const { position, orientation } = viewerPose.transform;
      viewerPosition.set(position.x, position.y, position.z);
      viewerQuaternion.set(orientation.x, orientation.y, orientation.z, orientation.w);
      viewerForward.copy(XR_DEFAULT_FORWARD).applyQuaternion(viewerQuaternion);
    } else {
      camera.getWorldPosition(viewerPosition);
      camera.getWorldQuaternion(viewerQuaternion);
      camera.getWorldDirection(viewerForward);
    }

    viewerForward.y = 0;

    if (viewerForward.lengthSq() < 0.0001) {
      viewerForward.copy(XR_DEFAULT_FORWARD);
    } else {
      viewerForward.normalize();
    }
  }

  function resetViewFromCurrentPose() {
    updatePlanetFromCurrentPose();
    updateHudFromCurrentPose();
  }

  function updatePlanetFromCurrentPose() {
    viewerRight.crossVectors(viewerForward, XR_UP).normalize();

    xrRoot.position
      .copy(viewerPosition)
      .addScaledVector(viewerForward, state.planetDistance)
      .addScaledVector(viewerRight, state.planetSideOffset);
    xrRoot.position.y = viewerPosition.y + state.planetVerticalOffset;
    xrRoot.scale.setScalar(state.planetScale);
  }

  function updateHudFromCurrentPose() {
    viewerRight.crossVectors(viewerForward, XR_UP).normalize();

    hud.group.position
      .copy(viewerPosition)
      .addScaledVector(viewerForward, XR_PLANET_DISTANCE + state.hudForwardOffset)
      .addScaledVector(viewerRight, state.hudSideOffset);
    hud.group.position.y = viewerPosition.y + state.hudVerticalOffset;
    hud.group.scale.setScalar(XR_HUD_SCALE);
    hudNormal.copy(viewerForward).multiplyScalar(-1);
    hudQuaternion.setFromUnitVectors(XR_PLANE_NORMAL, hudNormal);
    hud.group.quaternion.copy(hudQuaternion);
  }

  function updateXrStars() {
    xrStars.position.copy(viewerPosition);
  }

  function updateHeadLockedMarker() {
    xrHeadLockedMarker.position
      .copy(XR_HEAD_MARKER_OFFSET)
      .applyQuaternion(viewerQuaternion)
      .add(viewerPosition);
    xrHeadLockedMarker.quaternion.copy(viewerQuaternion);
  }

  function startPlanetGrab(controller, worldPoint) {
    if (!controller || !worldPoint) {
      return;
    }

    activeGrabController = controller;
    grabPreviousPoint
      .copy(worldPoint)
      .applyMatrix4(xrPlanetMesh.matrixWorld.clone().invert())
      .normalize();
  }

  function stopPlanetGrab(controller = null) {
    if (!controller || controller === activeGrabController) {
      activeGrabController = null;
    }
  }

  function startGripDrag(controller) {
    if (!controller || !renderer.xr.isPresenting) {
      return;
    }

    stopPlanetGrab(controller);
    setRayFromController(raycaster, controller, tempMatrix);
    hud.group.updateMatrixWorld(true);
    xrRoot.updateMatrixWorld(true);

    const hudHits = raycaster.intersectObjects(hud.dragObjects, false);
    if (hudHits.length) {
      beginGripDrag(controller, 'hud', hudHits[0].point);
      return;
    }

    if (isRightHandController(controller)) {
      beginGripZoom(controller);
      return;
    }

    const planetHits = raycaster.intersectObject(xrPlanetMesh, false);
    if (planetHits.length) {
      beginGripDrag(controller, 'planet', planetHits[0].point);
    }
  }

  function beginGripDrag(controller, target, worldPoint) {
    viewerRight.crossVectors(viewerForward, XR_UP).normalize();
    gripDragPlane.setFromNormalAndCoplanarPoint(viewerForward, worldPoint);

    activeGripDrag = {
      controller,
      target,
      plane: gripDragPlane.clone(),
      startPoint: worldPoint.clone(),
      startRight: viewerRight.clone(),
      startHudSideOffset: state.hudSideOffset,
      startHudVerticalOffset: state.hudVerticalOffset,
      startPlanetSideOffset: state.planetSideOffset,
      startPlanetVerticalOffset: state.planetVerticalOffset,
    };
  }

  function beginGripZoom(controller) {
    controller.updateMatrixWorld(true);
    gripZoomControllerPosition.setFromMatrixPosition(controller.matrixWorld);
    reportStatus('Right grip zoom active');

    activeGripDrag = {
      controller,
      target: 'zoom',
      startPlanetDistance: state.planetDistance,
      startControllerForwardDistance: gripZoomControllerPosition
        .sub(viewerPosition)
        .dot(viewerForward),
    };
  }

  function stopGripDrag(controller = null) {
    if (!controller || controller === activeGripDrag?.controller) {
      activeGripDrag = null;
    }
  }

  function updateGripDrag(controller) {
    if (!activeGripDrag || controller !== activeGripDrag.controller) {
      return;
    }

    setRayFromController(raycaster, controller, tempMatrix);

    if (activeGripDrag.target === 'zoom') {
      controller.updateMatrixWorld(true);
      gripZoomControllerPosition.setFromMatrixPosition(controller.matrixWorld);

      const currentForwardDistance = gripZoomControllerPosition
        .sub(viewerPosition)
        .dot(viewerForward);
      const forwardDelta = currentForwardDistance - activeGripDrag.startControllerForwardDistance;

      updateSpatialState({
        planetDistance: THREE.MathUtils.clamp(
          activeGripDrag.startPlanetDistance - (forwardDelta * XR_R1_ZOOM_DISTANCE_SCALE),
          XR_PLANET_MIN_DISTANCE,
          XR_PLANET_MAX_DISTANCE
        ),
      });
      updatePlanetFromCurrentPose();
      return;
    }

    if (!raycaster.ray.intersectPlane(activeGripDrag.plane, gripDragPoint)) {
      return;
    }

    gripDragDelta.subVectors(gripDragPoint, activeGripDrag.startPoint);

    if (activeGripDrag.target === 'hud') {
      updateSpatialState({
        hudSideOffset: THREE.MathUtils.clamp(
          activeGripDrag.startHudSideOffset + gripDragDelta.dot(activeGripDrag.startRight),
          1.2,
          3.35
        ),
        hudVerticalOffset: THREE.MathUtils.clamp(
          activeGripDrag.startHudVerticalOffset + gripDragDelta.y,
          -0.78,
          0.68
        ),
      });
      updateHudFromCurrentPose();
      return;
    }

    updateSpatialState({
      planetSideOffset: THREE.MathUtils.clamp(
        activeGripDrag.startPlanetSideOffset + gripDragDelta.dot(activeGripDrag.startRight),
        -1.4,
        1.4
      ),
      planetVerticalOffset: THREE.MathUtils.clamp(
        activeGripDrag.startPlanetVerticalOffset + gripDragDelta.y,
        -0.85,
        0.85
      ),
    });
    updatePlanetFromCurrentPose();
  }

  function updatePlanetGrab(controller) {
    if (controller !== activeGrabController) {
      return;
    }

    setRayFromController(raycaster, controller, tempMatrix);
    xrRoot.updateMatrixWorld(true);

    const planetHits = raycaster.intersectObject(xrPlanetMesh, false);
    if (!planetHits.length) {
      return;
    }

    grabCurrentPoint
      .copy(planetHits[0].point)
      .applyMatrix4(xrPlanetMesh.matrixWorld.clone().invert())
      .normalize();

    if (grabPreviousPoint.dot(grabCurrentPoint) > 0.9998) {
      return;
    }

    grabDeltaQuaternion.setFromUnitVectors(grabPreviousPoint, grabCurrentPoint);
    xrPlanetPivot.quaternion.multiply(grabDeltaQuaternion);
    grabPreviousPoint.copy(grabCurrentPoint);
  }

  function dispose() {
    renderer.xr.removeEventListener('sessionstart', onSessionStart);
    renderer.xr.removeEventListener('sessionend', onSessionEnd);
    hud.dispose();
    disposeObject(xrRoot, { disposeMaps: false });
    xrFallbackPlanetTexture?.dispose();
    disposeObject(xrHeadLockedMarker);
    disposeObject(xrStars);
    velocitySurfaceTexture?.dispose();
    velocityDiagnosticTexture?.dispose();
    velocitySurfaceSource = null;
    velocitySurfaceTexture = null;
    velocityDiagnosticSource = null;
    velocityDiagnosticTexture = null;
    velocityLayerSamplerSources = [];
    velocityLayerSamplers = [];
    xrScene.remove(xrRoot);
    xrScene.remove(xrHeadLockedMarker);
    xrScene.remove(hud.group);
    xrScene.remove(xrStars);
    controllers.forEach((controller) => {
      xrScene.remove(controller);
    });
  }

  function getScene() {
    return xrScene;
  }

  function syncXrPlanetMaterial() {
    const sourcePlanet = getPlanetMesh?.();
    const sourceMaterial = sourcePlanet?.material;
    const surfaceMap = Array.isArray(sourceMaterial) ? null : sourceMaterial?.map ?? null;
    const useDarkGraySurface = state.xrSurfaceStyle === XR_SURFACE_STYLES.darkGray;
    const velocityMap = surfaceMap || useDarkGraySurface ? null : getVelocitySurfaceTexture();
    const nextMap = useDarkGraySurface ? null : surfaceMap ?? velocityMap ?? xrFallbackPlanetTexture;

    if (xrPlanetMesh.material.map !== nextMap) {
      xrPlanetMesh.material.map = nextMap;
      xrPlanetMesh.material.needsUpdate = true;
    }

    xrPlanetMesh.material.color.set(useDarkGraySurface ? XR_PLANET_DARK_GRAY_COLOR : XR_PLANET_COLOR);
    xrPlanetMesh.material.transparent = false;
    xrPlanetMesh.material.opacity = 1;
    xrPlanetMesh.material.depthTest = true;
    xrPlanetMesh.material.depthWrite = true;

    const shouldOccludeWind = Boolean(state.surfaceOpaque);
    xrWindTrailLayers.forEach((layer) => {
      layer.mesh.material.depthTest = shouldOccludeWind;
    });
    xrFlowDiagnosticShell.material.depthTest = shouldOccludeWind;
  }

  function syncXrFlowDiagnosticShell() {
    if (!state.showWinds) {
      xrFlowDiagnosticShell.visible = false;
      return;
    }

    const diagnosticTexture = getVelocityDiagnosticTexture();
    const shouldShowDiagnostic = Boolean(diagnosticTexture);

    xrFlowDiagnosticShell.visible = shouldShowDiagnostic;
    if (xrFlowDiagnosticShell.material.map !== diagnosticTexture) {
      xrFlowDiagnosticShell.material.map = diagnosticTexture;
      xrFlowDiagnosticShell.material.needsUpdate = true;
    }
    xrFlowDiagnosticShell.material.opacity = state.surfaceOpaque
      ? XR_FLOW_DIAGNOSTIC_OPACITY
      : XR_FLOW_DIAGNOSTIC_OPACITY * 0.72;
  }

  function getVelocitySurfaceTexture() {
    const velocityTexture = getVelocityTexture?.();
    const nextRange = getActiveWindMagnitudeRange(state);
    const nextRangeKey = formatWindMagnitudeRangeKey(nextRange);

    if (!velocityTexture?.image) {
      return null;
    }

    if (
      velocityTexture === velocitySurfaceSource &&
      nextRangeKey === velocitySurfaceRangeKey &&
      velocitySurfaceTexture
    ) {
      return velocitySurfaceTexture;
    }

    velocitySurfaceTexture?.dispose();
    velocitySurfaceSource = velocityTexture;
    velocitySurfaceRangeKey = nextRangeKey;
    velocitySurfaceTexture = createVelocitySurfaceTexture(velocityTexture, nextRange);

    return velocitySurfaceTexture;
  }

  function getVelocityDiagnosticTexture() {
    const velocityTexture = getVelocityTexture?.();
    const nextRange = getActiveWindMagnitudeRange(state);
    const nextRangeKey = formatWindMagnitudeRangeKey(nextRange);

    if (!velocityTexture?.image) {
      return null;
    }

    if (
      velocityTexture === velocityDiagnosticSource &&
      nextRangeKey === velocityDiagnosticRangeKey &&
      velocityDiagnosticTexture
    ) {
      return velocityDiagnosticTexture;
    }

    velocityDiagnosticTexture?.dispose();
    velocityDiagnosticSource = velocityTexture;
    velocityDiagnosticRangeKey = nextRangeKey;
    velocityDiagnosticTexture = createVelocityDiagnosticTexture(velocityTexture, nextRange);

    return velocityDiagnosticTexture;
  }

  function updateXrWindTracers(delta) {
    const shouldShowTracers = Boolean(state.showWinds);

    xrWindTrailGroup.visible = shouldShowTracers;
    xrWindTrailLayers.forEach((layer) => {
      layer.mesh.visible = shouldShowTracers;
    });

    if (!shouldShowTracers) {
      windTracerAccumulatedDelta = 0;
      return;
    }

    const samplers = getVelocityLayerSamplers();
    const windMagnitudeRange = getActiveWindMagnitudeRange(state);

    syncXrWindTracerLayers(samplers, windMagnitudeRange);
    xrWindTrailLayers.forEach((layer) => {
      layer.mesh.visible = shouldShowTracers;
    });

    windTracerAccumulatedDelta += delta;
    const updateInterval = getWindTracerUpdateInterval(
      state.windTracerQuality,
      xrWindTrailLayers.length
    );

    if (windTracerAccumulatedDelta < updateInterval) {
      return;
    }

    const tracerDelta = Math.min(
      windTracerAccumulatedDelta,
      XR_WIND_TRACER_MAX_ACCUMULATED_DELTA
    );
    windTracerAccumulatedDelta = 0;

    xrWindTrailLayers.forEach((layer) => {
      advanceWindTracers(
        layer.mesh,
        layer.sampler,
        windMagnitudeRange,
        tracerDelta
      );
    });
  }

  function syncXrWindTracerLayers(samplers, windMagnitudeRange) {
    const layerCount = Math.max(samplers.length, 1);
    const nextRangeKey = formatWindMagnitudeRangeKey(windMagnitudeRange);

    if (xrWindTrailLayers.length !== layerCount) {
      rebuildXrWindTrailLayers(layerCount);
    }

    xrWindTrailLayers.forEach((layer, index) => {
      const sampler = samplers[index] ?? null;
      const nextUsesSynthetic = !sampler;
      const nextQualityKey = formatWindTracerQualityKey(getWindTracerConfig(layer.mesh));

      if (
        layer.mesh.userData.tracers &&
        sampler === layer.sampler &&
        nextRangeKey === layer.rangeKey &&
        nextUsesSynthetic === layer.usesSynthetic &&
        nextQualityKey === layer.qualityKey
      ) {
        return;
      }

      seedWindTracers(layer.mesh, sampler, windMagnitudeRange);
      layer.sampler = sampler;
      layer.rangeKey = nextRangeKey;
      layer.usesSynthetic = nextUsesSynthetic;
      layer.qualityKey = nextQualityKey;
    });
  }

  function rebuildXrWindTrailLayers(layerCount) {
    const baseQuality = getWindTracerQualityPresetByLabel(state.windTracerQuality);

    windTracerAccumulatedDelta = 0;
    xrWindTrailLayers.forEach((layer) => {
      xrWindTrailGroup.remove(layer.mesh);
      disposeObject(layer.mesh);
    });

    xrWindTrailLayers = createXrWindTrailLayerEntries(layerCount, baseQuality);
    xrWindTrailLayers.forEach((layer) => {
      layer.mesh.material.depthTest = Boolean(state.surfaceOpaque);
      xrWindTrailGroup.add(layer.mesh);
    });
  }

  function getVelocityLayerSamplers() {
    const velocityTexture = getVelocityTexture?.();
    const layerSources = getVelocityLayerTextures(velocityTexture);

    if (!layerSources.length) {
      velocityLayerSamplerSources = [];
      velocityLayerSamplers = [];
      return [];
    }

    const hasSameSources =
      layerSources.length === velocityLayerSamplerSources.length &&
      layerSources.every((source, index) => source === velocityLayerSamplerSources[index]);

    if (hasSameSources) {
      return velocityLayerSamplers;
    }

    velocityLayerSamplerSources = layerSources;
    velocityLayerSamplers = layerSources
      .map((source) => createVelocitySampler(source))
      .filter(Boolean);

    return velocityLayerSamplers;
  }

  renderer.xr.addEventListener('sessionstart', onSessionStart);
  renderer.xr.addEventListener('sessionend', onSessionEnd);
  hud.update(state);
  reportStatus('Ready');

  return {
    beforeFrame,
    dispose,
    enterVR,
    getScene,
    updateState,
  };
}

function getActiveWindMagnitudeRange({
  windMagnitudeRange,
  windMagnitudeLimit,
} = {}) {
  return normaliseWindMagnitudeRange(
    windMagnitudeRange ?? windMagnitudeLimit ?? XR_SYNTHETIC_WIND_MAX_MAGNITUDE
  );
}

function createInitialStationTelemetry() {
  return {
    fps: null,
    frameMs: null,
    status: 'Calibrating',
    autoFlowFallback: false,
  };
}

function getStationPerformanceStatus(fps) {
  if (!Number.isFinite(fps)) {
    return 'Calibrating';
  }

  if (fps >= 60) {
    return 'Nominal';
  }

  if (fps >= XR_STATION_LOW_FPS_THRESHOLD) {
    return 'Watch';
  }

  return 'Low FPS';
}

function formatWindMagnitudeRangeKey({ min, max }) {
  return `${Math.round(min * 1000)}:${Math.round(max * 1000)}`;
}

function reportStatusFactory(onStatusChange) {
  return (message) => {
    onStatusChange?.(message);

    if (typeof window !== 'undefined') {
      window.__OASIS_XR_STATUS__ = message;
    }
  };
}

function formatXrError(error) {
  const name = error?.name ? `${error.name}: ` : '';
  return `${name}${error?.message ?? 'Unable to start VR mode.'}`;
}

function withTimeout(promise, timeoutMs, timeoutMessage, callbacks = {}) {
  let settled = false;
  let timedOut = false;

  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      if (settled) {
        return;
      }

      settled = true;
      timedOut = true;
      callbacks.onTimeout?.();
      reject(new Error(timeoutMessage));
    }, timeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        if (timedOut) {
          callbacks.onLateResolve?.(value);
          return;
        }

        settled = true;
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (timedOut) {
          return;
        }

        settled = true;
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function endXrSessionQuietly(session) {
  try {
    const result = session?.end?.();

    if (result && typeof result.catch === 'function') {
      result.catch(() => {});
    }
  } catch (error) {
    // Ignore cleanup failures after a startup timeout.
  }
}

function createXrPlanetMesh() {
  const geometry = new THREE.SphereGeometry(1, 96, 48);
  const material = new THREE.MeshBasicMaterial({
    color: XR_PLANET_COLOR,
    map: createXrFallbackPlanetTexture(),
    transparent: false,
    opacity: 1,
    depthTest: true,
    depthWrite: true,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'xr-safe-planet';
  return mesh;
}

function createXrAtmosphereShell() {
  const geometry = new THREE.SphereGeometry(XR_ATMOSPHERE_RADIUS, 96, 48);
  const material = new THREE.ShaderMaterial({
    vertexShader: `
      varying float vRim;

      void main() {
        vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
        vec3 viewNormal = normalize(normalMatrix * normal);
        vec3 viewDirection = normalize(-viewPosition.xyz);
        vRim = pow(1.0 - max(dot(viewNormal, viewDirection), 0.0), 2.35);
        gl_Position = projectionMatrix * viewPosition;
      }
    `,
    fragmentShader: `
      varying float vRim;

      void main() {
        float alpha = smoothstep(0.18, 0.96, vRim) * 0.34;

        if (alpha < 0.01) {
          discard;
        }

        vec3 color = mix(vec3(0.08, 0.48, 0.92), vec3(0.58, 0.9, 1.0), vRim);
        gl_FragColor = vec4(color, alpha);
        #include <colorspace_fragment>
      }
    `,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.BackSide,
  });
  const shell = new THREE.Mesh(geometry, material);
  shell.name = 'xr-atmosphere-rim-shell';
  shell.renderOrder = 12;
  shell.frustumCulled = false;

  return shell;
}

function createXrFlowDiagnosticShell() {
  const geometry = new THREE.SphereGeometry(XR_FLOW_DIAGNOSTIC_RADIUS, 96, 48);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: XR_FLOW_DIAGNOSTIC_OPACITY,
    side: THREE.FrontSide,
    depthTest: true,
    depthWrite: false,
  });
  const shell = new THREE.Mesh(geometry, material);
  shell.name = 'xr-flow-diagnostic-speed-veil';
  shell.renderOrder = 16;
  shell.frustumCulled = false;
  shell.visible = false;
  return shell;
}

function createXrFallbackPlanetTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 512;
  const context = canvas.getContext('2d');
  const bands = [
    '#1a2635',
    '#223246',
    '#121c2a',
    '#2c3c4f',
    '#172232',
    '#263951',
    '#101925',
    '#1f3044',
  ];

  bands.forEach((color, index) => {
    context.fillStyle = color;
    context.fillRect(0, index * 64, canvas.width, 64);
  });

  const glow = context.createRadialGradient(260, 190, 20, 260, 190, 210);
  glow.addColorStop(0, 'rgba(116, 176, 215, 0.24)');
  glow.addColorStop(0.45, 'rgba(42, 90, 128, 0.12)');
  glow.addColorStop(1, 'rgba(3, 9, 19, 0)');
  context.fillStyle = glow;
  context.fillRect(0, 0, canvas.width, canvas.height);

  context.fillStyle = 'rgba(3, 9, 19, 0.28)';
  for (let index = 0; index < 18; index += 1) {
    const y = 18 + (index * 27);
    context.fillRect(0, y, canvas.width, 3);
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.name = 'xr-fallback-banded-planet';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;

  return texture;
}

function createXrWindTrailLayerEntries(layerCount, baseQuality = XR_INITIAL_WIND_TRACER_QUALITY) {
  const resolvedLayerCount = Math.max(Math.round(layerCount), 1);
  const layerQuality = getWindTracerLayerQuality(baseQuality, resolvedLayerCount);

  return Array.from({ length: resolvedLayerCount }, (_, index) => ({
    mesh: createXrWindTrails(layerQuality, getWindTracerLayerRadius(index), index),
    sampler: null,
    rangeKey: '',
    usesSynthetic: true,
    qualityKey: '',
  }));
}

function getWindTracerLayerQuality(baseQuality, layerCount) {
  const countScale = layerCount <= 1
    ? 1
    : Math.max(0.16, 1 / Math.pow(layerCount, 0.95));

  return Object.freeze({
    ...baseQuality,
    count: Math.max(256, Math.round(baseQuality.count * countScale)),
  });
}

function getWindTracerLayerRadius(layerIndex) {
  return XR_WIND_TRACER_RADIUS + (layerIndex * XR_WIND_LAYER_ALTITUDE_STEP);
}

function getWindTracerUpdateInterval(qualityLabel, layerCount) {
  if (qualityLabel === XR_WIND_TRACER_QUALITY_PRESETS.performance.label) {
    return layerCount >= 5 ? 1 / 28 : 1 / 34;
  }

  if (layerCount >= 5) {
    return 1 / 34;
  }

  if (layerCount >= 3) {
    return 1 / 40;
  }

  return 1 / 50;
}

function createXrWindTrails(
  quality = XR_INITIAL_WIND_TRACER_QUALITY,
  radius = XR_WIND_TRACER_RADIUS,
  layerIndex = 0
) {
  const geometry = createWindTracerGeometry(quality);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uBaseOpacity: { value: XR_WIND_TRACER_OPACITY },
    },
    vertexShader: `
      attribute vec3 color;
      attribute float aAlpha;
      attribute float aSide;
      varying vec3 vColor;
      varying float vAlpha;
      varying float vSide;

      void main() {
        vColor = color;
        vAlpha = aAlpha;
        vSide = aSide;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      uniform float uBaseOpacity;
      varying vec3 vColor;
      varying float vAlpha;
      varying float vSide;

      void main() {
        float edgeFade = 1.0 - smoothstep(0.18, 1.0, abs(vSide));
        float core = 1.0 - smoothstep(0.0, 0.32, abs(vSide));
        float alpha = uBaseOpacity * vAlpha * mix(0.2, 1.0, edgeFade);

        if (alpha < 0.006) {
          discard;
        }

        vec3 vividColor = pow(max(vColor, vec3(0.001)), vec3(0.52));
        float luminance = dot(vividColor, vec3(0.2126, 0.7152, 0.0722));
        vec3 saturatedColor = clamp(mix(vec3(luminance), vividColor, 2.45), 0.0, 1.0);
        vec3 finalColor = min(saturatedColor * (1.72 + (1.18 * vAlpha) + (0.78 * core)), vec3(1.0));
        gl_FragColor = vec4(finalColor, alpha);
        #include <colorspace_fragment>
      }
    `,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  material.forceSinglePass = true;

  const tracers = new THREE.Mesh(geometry, material);
  tracers.name = `xr-wind-animated-tracers-layer-${layerIndex}`;
  tracers.frustumCulled = false;
  tracers.renderOrder = 20 + layerIndex;
  tracers.visible = false;
  tracers.userData.windTracerQuality = quality;
  tracers.userData.windTracerRadius = radius;
  tracers.userData.windTracerLayerIndex = layerIndex;
  tracers.userData.tracers = createWindTracerState(quality);

  return tracers;
}

function applyWindTracerQuality(mesh, quality) {
  mesh.geometry.dispose();
  mesh.geometry = createWindTracerGeometry(quality);
  mesh.userData.windTracerQuality = quality;
  mesh.userData.tracers = createWindTracerState(quality);
}

function getWindTracerConfig(mesh) {
  return mesh.userData.windTracerQuality ?? XR_INITIAL_WIND_TRACER_QUALITY;
}

function createWindTracerGeometry(quality) {
  const geometry = new THREE.BufferGeometry();
  const segmentCount = quality.count * quality.trailSegments;
  const vertexCount = segmentCount * 4;
  const indexCount = segmentCount * 6;
  const positions = new Float32Array(vertexCount * 3);
  const colors = new Float32Array(vertexCount * 3);
  const alphas = new Float32Array(vertexCount);
  const sides = new Float32Array(vertexCount);
  const indices = vertexCount > 65535 ? new Uint32Array(indexCount) : new Uint16Array(indexCount);

  for (let segment = 0; segment < segmentCount; segment += 1) {
    const vertex = segment * 4;
    const offset = segment * 6;

    indices[offset] = vertex;
    indices[offset + 1] = vertex + 1;
    indices[offset + 2] = vertex + 2;
    indices[offset + 3] = vertex + 1;
    indices[offset + 4] = vertex + 3;
    indices[offset + 5] = vertex + 2;
  }

  const positionAttribute = new THREE.BufferAttribute(positions, 3);
  const colorAttribute = new THREE.BufferAttribute(colors, 3);
  const alphaAttribute = new THREE.BufferAttribute(alphas, 1);
  const sideAttribute = new THREE.BufferAttribute(sides, 1);

  positionAttribute.setUsage(THREE.DynamicDrawUsage);
  colorAttribute.setUsage(THREE.DynamicDrawUsage);
  alphaAttribute.setUsage(THREE.DynamicDrawUsage);
  sideAttribute.setUsage(THREE.DynamicDrawUsage);

  geometry.setAttribute('position', positionAttribute);
  geometry.setAttribute('color', colorAttribute);
  geometry.setAttribute('aAlpha', alphaAttribute);
  geometry.setAttribute('aSide', sideAttribute);
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.boundingSphere = new THREE.Sphere(
    new THREE.Vector3(),
    XR_WIND_TRACER_RADIUS + XR_WIND_TRACER_WIDTH
  );

  return geometry;
}

function createWindTracerState(quality) {
  const historyLength = quality.trailSegments + 1;

  return {
    quality,
    positions: new Float32Array(quality.count * historyLength * 3),
    colors: new Float32Array(quality.count * historyLength * 3),
    magnitudes: new Float32Array(quality.count * historyLength),
    ages: new Float32Array(quality.count),
    maxAges: new Float32Array(quality.count),
    syntheticElapsed: 0,
  };
}

function seedWindTracers(mesh, sampler, windMagnitudeRange) {
  const quality = getWindTracerConfig(mesh);
  const tracers = mesh.userData.tracers?.quality === quality
    ? mesh.userData.tracers
    : createWindTracerState(quality);
  mesh.userData.tracers = tracers;
  tracers.syntheticElapsed = 0;

  for (let index = 0; index < tracers.quality.count; index += 1) {
    resetWindTracer(tracers, index, sampler, windMagnitudeRange);
  }

  writeWindTracerGeometry(mesh, windMagnitudeRange);
}

function advanceWindTracers(mesh, sampler, windMagnitudeRange, delta) {
  const tracers = mesh.userData.tracers;

  if (!tracers) {
    seedWindTracers(mesh, sampler, windMagnitudeRange);
    return;
  }

  tracers.syntheticElapsed += delta;

  for (let index = 0; index < tracers.quality.count; index += 1) {
    advanceWindTracer(tracers, index, sampler, windMagnitudeRange, delta);
  }

  writeWindTracerGeometry(mesh, windMagnitudeRange);
}

function advanceWindTracer(tracers, index, sampler, windMagnitudeRange, delta) {
  if (tracers.ages[index] <= 0 || Math.random() < XR_WIND_TRACER_RESPAWN_CHANCE) {
    resetWindTracer(tracers, index, sampler, windMagnitudeRange);
    return;
  }

  readWindTracerHistoryPosition(tracers, index, 0, tracerCurrent);

  if (tracerCurrent.lengthSq() < 0.5) {
    resetWindTracer(tracers, index, sampler, windMagnitudeRange);
    return;
  }

  tracerCurrent.normalize();

  const wind = sampleWindForTracer(
    tracerCurrent,
    sampler,
    index,
    0,
    tracers.syntheticElapsed,
    tracerWindA
  );
  const speed = Math.max(wind.magnitude, 0);

  if (speed < 0.00001) {
    resetWindTracer(tracers, index, sampler, windMagnitudeRange);
    return;
  }

  const motionSpeed = getWindTracerMotionSpeed(speed, windMagnitudeRange);
  const arcStep = Math.min(
    motionSpeed * delta * XR_WIND_TRACER_MOTION_SCALE,
    getWindTracerMaxArcStep(motionSpeed, windMagnitudeRange)
  );

  if (
    !advectUnitPosition(tracerMid, tracerCurrent, wind, arcStep * 0.5) ||
    !advectUnitPosition(
      tracerNext,
      tracerCurrent,
      sampleWindForTracer(
        tracerMid,
        sampler,
        index,
        0.5,
        tracers.syntheticElapsed,
        tracerWindB
      ),
      arcStep
    )
  ) {
    resetWindTracer(tracers, index, sampler, windMagnitudeRange);
    return;
  }

  shiftWindTracerHistory(tracers, index);
  writeWindTracerHistorySample(
    tracers,
    index,
    0,
    tracerNext,
    speed,
    windMagnitudeRange
  );
  tracers.ages[index] -= delta * XR_WIND_TRACER_AGE_RATE;
}

function resetWindTracer(tracers, index, sampler, windMagnitudeRange) {
  const lifetime = THREE.MathUtils.lerp(
    XR_WIND_TRACER_MIN_LIFETIME,
    XR_WIND_TRACER_MAX_LIFETIME,
    Math.random()
  );

  setWindTracerSeedPoint(
    tracerCurrent,
    sampler,
    index,
    tracers.syntheticElapsed,
    tracers.quality.count,
    windMagnitudeRange
  );
  tracers.ages[index] = lifetime;
  tracers.maxAges[index] = lifetime;

  for (let historyIndex = 0; historyIndex <= tracers.quality.trailSegments; historyIndex += 1) {
    const wind = sampleWindForTracer(
      tracerCurrent,
      sampler,
      index,
      historyIndex,
      tracers.syntheticElapsed,
      tracerWindA
    );
    const magnitude = Math.max(wind.magnitude, 0);

    writeWindTracerHistorySample(
      tracers,
      index,
      historyIndex,
      tracerCurrent,
      magnitude,
      windMagnitudeRange
    );

    if (
      historyIndex < tracers.quality.trailSegments &&
      advectUnitPosition(
        tracerNext,
        tracerCurrent,
        wind,
        -getWindTracerSeedArc(magnitude, windMagnitudeRange)
      )
    ) {
      tracerCurrent.copy(tracerNext);
    }
  }
}

function setFibonacciSpherePoint(target, index, totalCount, variant = 0, elapsed = 0) {
  const count = Math.max(Math.round(totalCount), 1);
  const epoch = Math.floor(elapsed * 0.06);
  const sampleIndex = positiveModulo(index + (variant * 1597) + (epoch * 3251), count);
  const y = 1 - (((sampleIndex + 0.5) / count) * 2);
  const theta = (sampleIndex + (variant * 0.38196601125) + (epoch * 0.17)) * XR_WIND_TRACER_GOLDEN_ANGLE;
  const radiusAtY = Math.sqrt(Math.max(1 - (y * y), 0));

  return target.set(
    radiusAtY * Math.cos(theta),
    y,
    radiusAtY * Math.sin(theta)
  ).normalize();
}

function setWindTracerSeedPoint(target, sampler, index, elapsed, totalCount, windMagnitudeRange) {
  setFibonacciSpherePoint(target, index, totalCount, 0, elapsed);

  if (!sampler) {
    return target;
  }

  const seedMode = getWindTracerSeedMode(index);

  if (seedMode === 'random') {
    return target;
  }

  let bestMagnitude = sampleWindForTracer(target, sampler, index, 0, elapsed, tracerWindA).magnitude;
  let bestScore = getWindTracerSeedScore(seedMode, bestMagnitude, windMagnitudeRange);

  for (let candidate = 1; candidate < XR_WIND_TRACER_SEED_CANDIDATES; candidate += 1) {
    setFibonacciSpherePoint(tracerSeedCandidate, index, totalCount, candidate, elapsed);

    const candidateMagnitude = sampleWindForTracer(
      tracerSeedCandidate,
      sampler,
      index,
      candidate,
      elapsed,
      tracerWindB
    ).magnitude;
    const candidateScore = getWindTracerSeedScore(
      seedMode,
      candidateMagnitude,
      windMagnitudeRange
    );

    if (candidateScore > bestScore) {
      bestMagnitude = candidateMagnitude;
      bestScore = candidateScore;
      target.copy(tracerSeedCandidate);
    }
  }

  return target;
}

function getWindTracerSeedMode(index) {
  const slot = index % 12;

  if (slot < 6) {
    return 'weak';
  }

  if (slot < 9) {
    return 'mid';
  }

  if (slot < 11) {
    return 'fast';
  }

  return 'random';
}

function getWindTracerSeedScore(seedMode, magnitude, windMagnitudeRange) {
  const speedRatio = getWindMagnitudeRatio(magnitude, windMagnitudeRange);

  if (seedMode === 'weak') {
    return 1 - speedRatio;
  }

  if (seedMode === 'mid') {
    return 1 - Math.abs(speedRatio - 0.42);
  }

  return speedRatio;
}

function sampleWindForTracer(position, sampler, index, step, elapsed, target) {
  return sampler
    ? sampleWindAtUnitPosition(position, sampler, target)
    : getSyntheticWindAtUnitPosition(position, index + Math.round(step * 37), elapsed, target);
}

function getWindTracerSeedArc(magnitude, windMagnitudeRange) {
  const speedRatio = getWindMagnitudeRatio(magnitude, windMagnitudeRange);

  return THREE.MathUtils.lerp(
    XR_WIND_TRACER_MIN_ARC_STEP * 2.1,
    XR_WIND_TRACER_MAX_ARC_STEP * 1.18,
    Math.pow(speedRatio, 0.38)
  );
}

function getWindTracerMotionSpeed(magnitude, windMagnitudeRange) {
  const range = normaliseWindMagnitudeRange(windMagnitudeRange);
  const motionFloor = (range.max - range.min) * XR_WIND_TRACER_MIN_MOTION_RATIO;

  return Math.max(magnitude, motionFloor);
}

function getWindTracerMaxArcStep(magnitude, windMagnitudeRange) {
  const speedRatio = getWindMagnitudeRatio(magnitude, windMagnitudeRange);

  return THREE.MathUtils.lerp(
    XR_WIND_TRACER_MIN_ARC_STEP,
    XR_WIND_TRACER_MAX_ARC_STEP,
    Math.pow(speedRatio, 0.56)
  );
}

function advectUnitPosition(target, position, wind, signedArc) {
  if (!setWindDirectionAtUnitPosition(streamDirection, position, wind)) {
    return false;
  }

  target
    .copy(position)
    .normalize()
    .multiplyScalar(Math.cos(signedArc))
    .addScaledVector(streamDirection, Math.sin(signedArc))
    .normalize();

  return true;
}

function shiftWindTracerHistory(tracers, index) {
  for (let historyIndex = tracers.quality.trailSegments; historyIndex > 0; historyIndex -= 1) {
    const targetOffset = getWindTracerPositionOffset(tracers, index, historyIndex);
    const sourceOffset = getWindTracerPositionOffset(tracers, index, historyIndex - 1);

    tracers.positions[targetOffset] = tracers.positions[sourceOffset];
    tracers.positions[targetOffset + 1] = tracers.positions[sourceOffset + 1];
    tracers.positions[targetOffset + 2] = tracers.positions[sourceOffset + 2];
    tracers.magnitudes[getWindTracerMagnitudeOffset(tracers, index, historyIndex)] =
      tracers.magnitudes[getWindTracerMagnitudeOffset(tracers, index, historyIndex - 1)];

    const targetColorOffset = getWindTracerColorOffset(tracers, index, historyIndex);
    const sourceColorOffset = getWindTracerColorOffset(tracers, index, historyIndex - 1);

    tracers.colors[targetColorOffset] = tracers.colors[sourceColorOffset];
    tracers.colors[targetColorOffset + 1] = tracers.colors[sourceColorOffset + 1];
    tracers.colors[targetColorOffset + 2] = tracers.colors[sourceColorOffset + 2];
  }
}

function readWindTracerHistoryPosition(tracers, index, historyIndex, target) {
  const offset = getWindTracerPositionOffset(tracers, index, historyIndex);

  return target.set(
    tracers.positions[offset],
    tracers.positions[offset + 1],
    tracers.positions[offset + 2]
  );
}

function writeWindTracerHistoryPosition(tracers, index, historyIndex, position) {
  const offset = getWindTracerPositionOffset(tracers, index, historyIndex);

  tracers.positions[offset] = position.x;
  tracers.positions[offset + 1] = position.y;
  tracers.positions[offset + 2] = position.z;
}

function writeWindTracerHistorySample(
  tracers,
  index,
  historyIndex,
  position,
  magnitude,
  windMagnitudeRange
) {
  writeWindTracerHistoryPosition(tracers, index, historyIndex, position);

  tracers.magnitudes[getWindTracerMagnitudeOffset(tracers, index, historyIndex)] = magnitude;

  const colorOffset = getWindTracerColorOffset(tracers, index, historyIndex);

  writeWindTracerColor(tracers.colors, colorOffset, magnitude, windMagnitudeRange);
}

function getWindTracerPositionOffset(tracers, index, historyIndex) {
  return ((index * (tracers.quality.trailSegments + 1)) + historyIndex) * 3;
}

function getWindTracerMagnitudeOffset(tracers, index, historyIndex) {
  return (index * (tracers.quality.trailSegments + 1)) + historyIndex;
}

function getWindTracerColorOffset(tracers, index, historyIndex) {
  return getWindTracerPositionOffset(tracers, index, historyIndex);
}

function writeWindTracerGeometry(mesh, windMagnitudeRange) {
  const tracers = mesh.userData.tracers;
  const geometry = mesh.geometry;
  const positions = geometry.attributes.position.array;
  const colors = geometry.attributes.color.array;
  const alphas = geometry.attributes.aAlpha.array;
  const sides = geometry.attributes.aSide.array;
  const tracerRadius = mesh.userData.windTracerRadius ?? XR_WIND_TRACER_RADIUS;

  for (let index = 0; index < tracers.quality.count; index += 1) {
    const lifeFade = getWindTracerLifeFade(tracers.ages[index], tracers.maxAges[index]);

    for (let segment = 0; segment < tracers.quality.trailSegments; segment += 1) {
      writeWindTracerSegmentGeometry(
        tracers,
        index,
        segment,
        lifeFade,
        windMagnitudeRange,
        positions,
        colors,
        alphas,
        sides,
        tracerRadius
      );
    }
  }

  geometry.attributes.position.needsUpdate = true;
  geometry.attributes.color.needsUpdate = true;
  geometry.attributes.aAlpha.needsUpdate = true;
  geometry.attributes.aSide.needsUpdate = true;
}

function writeWindTracerSegmentGeometry(
  tracers,
  index,
  segment,
  lifeFade,
  windMagnitudeRange,
  positions,
  colors,
  alphas,
  sides,
  tracerRadius
) {
  readWindTracerHistoryPosition(tracers, index, segment, tracerCenterA).normalize();
  readWindTracerHistoryPosition(tracers, index, segment + 1, tracerCenterB).normalize();
  tracerTangent.subVectors(tracerCenterA, tracerCenterB);
  tracerTangent.addScaledVector(tracerCenterA, -tracerTangent.dot(tracerCenterA));

  if (tracerTangent.lengthSq() < 0.000001) {
    writeDegenerateWindTracerSegment(tracers, index, segment, positions, alphas, sides);
    return;
  }

  tracerTangent.normalize();
  tracerSideA.crossVectors(tracerCenterA, tracerTangent);
  tracerSideB.crossVectors(tracerCenterB, tracerTangent);

  if (tracerSideA.lengthSq() < 0.000001 || tracerSideB.lengthSq() < 0.000001) {
    writeDegenerateWindTracerSegment(tracers, index, segment, positions, alphas, sides);
    return;
  }

  tracerSideA.normalize();
  tracerSideB.normalize();

  const magnitudeA = tracers.magnitudes[getWindTracerMagnitudeOffset(tracers, index, segment)];
  const magnitudeB = tracers.magnitudes[getWindTracerMagnitudeOffset(tracers, index, segment + 1)];
  const speedRatioA = getWindMagnitudeRatio(magnitudeA, windMagnitudeRange);
  const speedRatioB = getWindMagnitudeRatio(magnitudeB, windMagnitudeRange);
  const trailProgressA = segment / tracers.quality.trailSegments;
  const trailProgressB = (segment + 1) / tracers.quality.trailSegments;
  const fadeA = lifeFade * getWindTracerTailFade(trailProgressA) * getWindTracerSpeedVisibility(speedRatioA);
  const fadeB = lifeFade * getWindTracerTailFade(trailProgressB) * getWindTracerSpeedVisibility(speedRatioB);
  const widthA = getWindTracerRibbonWidth(speedRatioA, fadeA);
  const widthB = getWindTracerRibbonWidth(speedRatioB, fadeB);
  const colorOffsetA = getWindTracerColorOffset(tracers, index, segment);
  const colorOffsetB = getWindTracerColorOffset(tracers, index, segment + 1);
  const vertexBase = ((index * tracers.quality.trailSegments) + segment) * 4;

  writeWindTracerVertex(positions, colors, alphas, sides, vertexBase, tracerCenterA, tracerSideA, widthA, 1, tracers.colors, colorOffsetA, fadeA, tracerRadius);
  writeWindTracerVertex(positions, colors, alphas, sides, vertexBase + 1, tracerCenterA, tracerSideA, widthA, -1, tracers.colors, colorOffsetA, fadeA, tracerRadius);
  writeWindTracerVertex(positions, colors, alphas, sides, vertexBase + 2, tracerCenterB, tracerSideB, widthB, 1, tracers.colors, colorOffsetB, fadeB, tracerRadius);
  writeWindTracerVertex(positions, colors, alphas, sides, vertexBase + 3, tracerCenterB, tracerSideB, widthB, -1, tracers.colors, colorOffsetB, fadeB, tracerRadius);
}

function writeWindTracerVertex(
  positions,
  colors,
  alphas,
  sides,
  vertexIndex,
  center,
  side,
  width,
  sideSign,
  tracerColors,
  colorOffset,
  alpha,
  tracerRadius
) {
  tracerLeft
    .copy(center)
    .multiplyScalar(tracerRadius)
    .addScaledVector(side, width * sideSign * 0.5);

  const positionOffset = vertexIndex * 3;
  positions[positionOffset] = tracerLeft.x;
  positions[positionOffset + 1] = tracerLeft.y;
  positions[positionOffset + 2] = tracerLeft.z;
  colors[positionOffset] = tracerColors[colorOffset];
  colors[positionOffset + 1] = tracerColors[colorOffset + 1];
  colors[positionOffset + 2] = tracerColors[colorOffset + 2];
  alphas[vertexIndex] = alpha;
  sides[vertexIndex] = sideSign;
}

function writeDegenerateWindTracerSegment(tracers, index, segment, positions, alphas, sides) {
  const vertexBase = ((index * tracers.quality.trailSegments) + segment) * 4;

  for (let vertex = 0; vertex < 4; vertex += 1) {
    const vertexIndex = vertexBase + vertex;
    const positionOffset = vertexIndex * 3;

    positions[positionOffset] = 0;
    positions[positionOffset + 1] = 0;
    positions[positionOffset + 2] = 0;
    alphas[vertexIndex] = 0;
    sides[vertexIndex] = 0;
  }
}

function getWindTracerRibbonWidth(speedRatio, fade) {
  return XR_WIND_TRACER_WIDTH *
    THREE.MathUtils.lerp(0.54, 1.34, Math.sqrt(speedRatio)) *
    THREE.MathUtils.lerp(0.42, 1, THREE.MathUtils.clamp(fade, 0, 1));
}

function getWindTracerSpeedVisibility(speedRatio) {
  return THREE.MathUtils.lerp(0.72, 1.28, Math.sqrt(speedRatio));
}

function getWindTracerTailFade(progress) {
  return THREE.MathUtils.lerp(
    XR_WIND_TRACER_TAIL_MIN_FADE,
    1,
    Math.pow(1 - progress, XR_WIND_TRACER_TAIL_FADE_EXPONENT)
  );
}

function getWindTracerLifeFade(age, maxAge) {
  const birthFade = THREE.MathUtils.clamp((maxAge - age) / XR_WIND_TRACER_FADE_FRAMES, 0, 1);
  const deathFade = THREE.MathUtils.clamp(age / XR_WIND_TRACER_FADE_FRAMES, 0, 1);

  return birthFade * deathFade;
}

function createXrStarField() {
  const geometry = new THREE.BufferGeometry();
  const positions = new Float32Array(XR_STAR_COUNT * 3);
  const colors = new Float32Array(XR_STAR_COUNT * 3);
  const starPosition = new THREE.Vector3();

  for (let index = 0; index < XR_STAR_COUNT; index += 1) {
    const theta = Math.random() * Math.PI * 2;
    const y = (Math.random() * 2) - 1;
    const radiusAtY = Math.sqrt(Math.max(1 - (y * y), 0));
    const distance = THREE.MathUtils.lerp(
      XR_STAR_INNER_RADIUS,
      XR_STAR_OUTER_RADIUS,
      Math.pow(Math.random(), 0.42)
    );
    const brightness = THREE.MathUtils.lerp(0.38, 1, Math.pow(Math.random(), 0.35));
    const coolTint = THREE.MathUtils.lerp(0.82, 1, Math.random());

    starPosition
      .set(radiusAtY * Math.cos(theta), y, radiusAtY * Math.sin(theta))
      .multiplyScalar(distance);

    const offset = index * 3;
    positions[offset] = starPosition.x;
    positions[offset + 1] = starPosition.y;
    positions[offset + 2] = starPosition.z;
    colors[offset] = brightness * coolTint;
    colors[offset + 1] = brightness * THREE.MathUtils.lerp(0.88, 1, Math.random());
    colors[offset + 2] = brightness;
  }

  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

  const material = new THREE.PointsMaterial({
    vertexColors: true,
    transparent: true,
    opacity: 0.92,
    size: 0.045,
    sizeAttenuation: true,
    depthTest: true,
    depthWrite: false,
  });
  const stars = new THREE.Points(geometry, material);
  stars.name = 'xr-space-starfield';
  stars.frustumCulled = false;

  return stars;
}

function createHeadLockedMarker() {
  const group = new THREE.Group();
  group.name = 'xr-head-locked-visibility-marker';

  const markerMaterial = new THREE.MeshBasicMaterial({
    color: 0x7ad4ff,
    transparent: true,
    opacity: 0.86,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  const softMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.72,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });

  const ring = new THREE.Mesh(new THREE.RingGeometry(0.052, 0.067, 40), markerMaterial);
  const horizontal = new THREE.Mesh(new THREE.PlaneGeometry(0.18, 0.009), softMaterial);
  const vertical = new THREE.Mesh(new THREE.PlaneGeometry(0.009, 0.18), softMaterial);
  const label = createTexturedPlane(
    0.52,
    0.15,
    createSceneLabelTexture('OASIS scene active', 'data planet')
  );
  label.position.set(0, -0.16, 0.002);

  group.add(ring);
  group.add(horizontal);
  group.add(vertical);
  group.add(label);
  group.traverse((child) => {
    child.renderOrder = 1000;
  });

  return group;
}

function writeWindTracerColor(colors, colorOffset, magnitude, windMagnitudeRange = XR_SYNTHETIC_WIND_MAX_MAGNITUDE) {
  const position = getWindMagnitudeRatio(magnitude, windMagnitudeRange);
  let start = windTracerColorScale[0];
  let end = windTracerColorScale[windTracerColorScale.length - 1];

  for (let index = 0; index < windTracerColorScale.length - 1; index += 1) {
    const current = windTracerColorScale[index];
    const next = windTracerColorScale[index + 1];

    if (position >= current.position && position <= next.position) {
      start = current;
      end = next;
      break;
    }
  }

  const span = Math.max(end.position - start.position, 0.0001);
  const localPosition = (position - start.position) / span;

  colors[colorOffset] = (start.rgb.r + ((end.rgb.r - start.rgb.r) * localPosition)) / 255;
  colors[colorOffset + 1] = (start.rgb.g + ((end.rgb.g - start.rgb.g) * localPosition)) / 255;
  colors[colorOffset + 2] = (start.rgb.b + ((end.rgb.b - start.rgb.b) * localPosition)) / 255;
}

function setWindDirectionAtUnitPosition(target, position, wind) {
  const normalisedPosition = streamUnit.copy(position).normalize();
  const lon = Math.atan2(normalisedPosition.z, normalisedPosition.x);
  const lat = Math.asin(THREE.MathUtils.clamp(normalisedPosition.y, -1, 1));

  streamEast.set(-Math.sin(lon), 0, Math.cos(lon));
  streamNorth.set(
    -Math.sin(lat) * Math.cos(lon),
    Math.cos(lat),
    -Math.sin(lat) * Math.sin(lon)
  );
  target
    .copy(streamEast)
    .multiplyScalar(wind.lon)
    .addScaledVector(streamNorth, wind.lat);

  if (target.lengthSq() < 0.0001) {
    return false;
  }

  target.normalize();
  return true;
}

function getSyntheticWindAtUnitPosition(position, index, elapsed, target = tracerWindA) {
  const lon = Math.atan2(position.z, position.x);
  const lat = Math.asin(THREE.MathUtils.clamp(position.y, -1, 1));
  const band = Math.sin((lat * 6.0) + (elapsed * 0.9));
  const wave = Math.sin((lon * 3.2) + (elapsed * 1.15) + (index * 0.017));
  const shear = Math.cos((lat * 2.4) - (elapsed * 0.7) + (index * 0.009));
  const lonVelocity = XR_SYNTHETIC_WIND_MAX_MAGNITUDE * (0.45 + (0.32 * wave) + (0.16 * shear));
  const latVelocity = XR_SYNTHETIC_WIND_MAX_MAGNITUDE * ((0.18 * band) + (0.12 * wave));

  target.lon = lonVelocity;
  target.lat = latVelocity;
  target.magnitude = Math.sqrt((lonVelocity * lonVelocity) + (latVelocity * latVelocity));

  return target;
}

function createVelocitySampler(velocityTexture) {
  const sourceImage = velocityTexture.image;
  const sourceWidth = sourceImage?.naturalWidth || sourceImage?.videoWidth || sourceImage?.width || 0;
  const sourceHeight = sourceImage?.naturalHeight || sourceImage?.videoHeight || sourceImage?.height || 0;

  if (!sourceWidth || !sourceHeight) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = sourceWidth;
  canvas.height = sourceHeight;

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);

  try {
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    return createDecodedVelocitySampler(imageData);
  } catch (error) {
    return null;
  }
}

function createDecodedVelocitySampler(imageData) {
  const pixelCount = imageData.width * imageData.height;
  const lonData = new Float32Array(pixelCount);
  const latData = new Float32Array(pixelCount);
  const data = imageData.data;

  for (let offset = 0, pixel = 0; offset < data.length; offset += 4, pixel += 1) {
    const wind = decodeWindVector({
      r: data[offset] / 255,
      g: data[offset + 1] / 255,
      b: data[offset + 2] / 255,
      a: data[offset + 3] / 255,
    });

    lonData[pixel] = wind.lon;
    latData[pixel] = wind.lat;
  }

  return {
    width: imageData.width,
    height: imageData.height,
    lonData,
    latData,
  };
}

function getVelocityLayerTextures(velocityTexture) {
  if (!velocityTexture?.image) {
    return [];
  }

  const layerTextures = velocityTexture.userData?.layerTextures;

  if (Array.isArray(layerTextures) && layerTextures.length) {
    return layerTextures.filter((texture) => texture?.image);
  }

  return [velocityTexture];
}

function sampleWindAtUnitPosition(position, sampler, target = tracerWindA) {
  const normalisedY = THREE.MathUtils.clamp(position.y, -1, 1);
  const lon = (Math.atan2(position.z, position.x) + Math.PI) / (Math.PI * 2);
  const lat = (Math.asin(normalisedY) + (Math.PI / 2)) / Math.PI;
  const x = lon * (sampler.width - 1);
  const y = (1 - lat) * (sampler.height - 1);
  const x0 = positiveModulo(Math.floor(x), sampler.width);
  const x1 = positiveModulo(x0 + 1, sampler.width);
  const y0 = THREE.MathUtils.clamp(Math.floor(y), 0, sampler.height - 1);
  const y1 = THREE.MathUtils.clamp(y0 + 1, 0, sampler.height - 1);
  const tx = x - Math.floor(x);
  const ty = y - Math.floor(y);
  const topLeft = (y0 * sampler.width) + x0;
  const topRight = (y0 * sampler.width) + x1;
  const bottomLeft = (y1 * sampler.width) + x0;
  const bottomRight = (y1 * sampler.width) + x1;

  target.lon = bilerp(
    sampler.lonData[topLeft],
    sampler.lonData[topRight],
    sampler.lonData[bottomLeft],
    sampler.lonData[bottomRight],
    tx,
    ty
  );
  target.lat = bilerp(
    sampler.latData[topLeft],
    sampler.latData[topRight],
    sampler.latData[bottomLeft],
    sampler.latData[bottomRight],
    tx,
    ty
  );
  target.magnitude = Math.sqrt((target.lon * target.lon) + (target.lat * target.lat));

  return target;
}

function bilerp(topLeft, topRight, bottomLeft, bottomRight, tx, ty) {
  const top = THREE.MathUtils.lerp(topLeft, topRight, tx);
  const bottom = THREE.MathUtils.lerp(bottomLeft, bottomRight, tx);

  return THREE.MathUtils.lerp(top, bottom, ty);
}

function positiveModulo(value, modulus) {
  return ((value % modulus) + modulus) % modulus;
}

function createController(renderer, index) {
  const controller = renderer.xr.getController(index);
  controller.userData.xrIndex = index;
  controller.userData.xrButtonState = [];

  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -1),
  ]);
  const material = new THREE.LineBasicMaterial({
    color: 0x7ad4ff,
    transparent: true,
    opacity: 0.72,
    depthTest: false,
    depthWrite: false,
  });
  const ray = new THREE.Line(geometry, material);
  ray.name = 'controller-ray';
  ray.scale.z = XR_CONTROLLER_RAY_LENGTH;
  ray.renderOrder = 1302;
  ray.frustumCulled = false;

  const beam = new THREE.Mesh(
    new THREE.CylinderGeometry(0.006, 0.002, XR_CONTROLLER_RAY_LENGTH, 12, 1, true),
    new THREE.MeshBasicMaterial({
      color: 0x7ad4ff,
      transparent: true,
      opacity: 0.28,
      depthTest: false,
      depthWrite: false,
    })
  );
  beam.name = 'controller-ray-beam';
  beam.position.z = -XR_CONTROLLER_RAY_LENGTH / 2;
  beam.rotation.x = Math.PI / 2;
  beam.renderOrder = 1301;
  beam.frustumCulled = false;

  const tip = new THREE.Mesh(
    new THREE.SphereGeometry(0.025, 16, 8),
    new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.88,
      depthTest: false,
      depthWrite: false,
    })
  );
  tip.name = 'controller-ray-tip';
  tip.position.z = -XR_CONTROLLER_RAY_LENGTH;
  tip.renderOrder = 1303;
  tip.frustumCulled = false;

  controller.add(ray);
  controller.add(beam);
  controller.add(tip);
  return controller;
}

function setRayFromController(raycaster, controller, tempMatrix) {
  controller.updateMatrixWorld(true);
  tempMatrix.identity().extractRotation(controller.matrixWorld);
  raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
  raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);
}

function setRayFromInputSourceEvent(raycaster, event, renderer, inputSourceMatrix, tempMatrix) {
  const referenceSpace = renderer.xr.getReferenceSpace?.();
  const targetRaySpace = event?.inputSource?.targetRaySpace;
  const inputFrame = event?.frame;

  if (!referenceSpace || !targetRaySpace || !inputFrame || typeof inputFrame.getPose !== 'function') {
    return false;
  }

  const pose = inputFrame.getPose(targetRaySpace, referenceSpace);
  const matrix = pose?.transform?.matrix;

  if (!matrix) {
    return false;
  }

  inputSourceMatrix.fromArray(matrix);
  tempMatrix.identity().extractRotation(inputSourceMatrix);
  raycaster.ray.origin.setFromMatrixPosition(inputSourceMatrix);
  raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix).normalize();

  return true;
}

function findControllerForInputSource(controllers, inputSource) {
  if (!inputSource) {
    return null;
  }

  return controllers.find((controller) => getControllerInputSource(controller) === inputSource) ?? null;
}

function cacheControllerInputSource(controller, inputSource) {
  if (!controller || !inputSource) {
    return;
  }

  controller.userData.xrInputSource = inputSource;
  controller.userData.xrHandedness = inputSource.handedness;
}

function clearControllerInputSource(controller, inputSource = null) {
  if (!controller || (inputSource && controller.userData.xrInputSource !== inputSource)) {
    return;
  }

  controller.userData.xrInputSource = null;
  controller.userData.xrHandedness = null;
}

function getControllerInputSource(controller) {
  return controller?.userData?.xrInputSource ?? controller?.inputSource ?? null;
}

function isRightHandController(controller) {
  const handedness = getControllerInputSource(controller)?.handedness ?? controller?.userData?.xrHandedness;

  return handedness === 'right' || (!handedness && controller?.userData?.xrIndex === 1);
}

function pollControllerButtons(controller, onSelectEnd, onGripStart, onGripEnd) {
  const gamepad = getControllerInputSource(controller)?.gamepad;

  if (!gamepad?.buttons?.length) {
    controller.userData.xrButtonState = [];
    return;
  }

  const previousState = controller.userData.xrButtonState ?? [];

  gamepad.buttons.forEach((button, index) => {
    const wasPressed = Boolean(previousState[index]);
    const isPressed = Boolean(button?.pressed);

    if (isPressed && !wasPressed) {
      if (index === 0) {
        requestControllerButtonSelect(controller);
      } else if (index === 1) {
        onGripStart?.();
      }
    } else if (!isPressed && wasPressed && index === 0) {
      onSelectEnd?.();
    } else if (!isPressed && wasPressed && index === 1) {
      onGripEnd?.();
    }

    previousState[index] = isPressed;
  });

  controller.userData.xrButtonState = previousState;
}

function requestControllerButtonSelect(controller) {
  controller.dispatchEvent({ type: 'selectstart' });
}

function createVelocitySurfaceTexture(velocityTexture, windMagnitudeRange) {
  const sourceImage = velocityTexture.image;
  const sourceWidth = sourceImage?.naturalWidth || sourceImage?.videoWidth || sourceImage?.width || 0;
  const sourceHeight = sourceImage?.naturalHeight || sourceImage?.videoHeight || sourceImage?.height || 0;

  if (!sourceWidth || !sourceHeight) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.min(sourceWidth, 768);
  canvas.height = Math.min(sourceHeight, 384);

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);

  let imageData;

  try {
    imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  } catch (error) {
    return null;
  }

  const { data } = imageData;

  for (let index = 0; index < data.length; index += 4) {
    const windSample = {
      r: data[index] / 255,
      g: data[index + 1] / 255,
      b: data[index + 2] / 255,
      a: data[index + 3] / 255,
    };
    const { magnitude } = decodeWindVector(windSample);
    const { r, g, b } = hexToRgb(getWindColor(magnitude, windMagnitudeRange));

    data[index] = r;
    data[index + 1] = g;
    data[index + 2] = b;
    data[index + 3] = 255;
  }

  context.putImageData(imageData, 0, 0);

  const surfaceTexture = new THREE.CanvasTexture(canvas);
  surfaceTexture.name = 'xr-velocity-surface';
  surfaceTexture.colorSpace = THREE.SRGBColorSpace;
  surfaceTexture.minFilter = THREE.LinearFilter;
  surfaceTexture.magFilter = THREE.LinearFilter;
  surfaceTexture.generateMipmaps = false;
  surfaceTexture.wrapS = THREE.RepeatWrapping;
  surfaceTexture.wrapT = THREE.ClampToEdgeWrapping;
  surfaceTexture.needsUpdate = true;

  return surfaceTexture;
}

function createVelocityDiagnosticTexture(velocityTexture, windMagnitudeRange) {
  const sourceImage = velocityTexture.image;
  const sourceWidth = sourceImage?.naturalWidth || sourceImage?.videoWidth || sourceImage?.width || 0;
  const sourceHeight = sourceImage?.naturalHeight || sourceImage?.videoHeight || sourceImage?.height || 0;

  if (!sourceWidth || !sourceHeight) {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.min(sourceWidth, 384);
  canvas.height = Math.min(sourceHeight, 192);

  const context = canvas.getContext('2d', { willReadFrequently: true });

  if (!context) {
    return null;
  }

  context.drawImage(sourceImage, 0, 0, canvas.width, canvas.height);

  let imageData;

  try {
    imageData = context.getImageData(0, 0, canvas.width, canvas.height);
  } catch (error) {
    return null;
  }

  const { data } = imageData;

  for (let index = 0; index < data.length; index += 4) {
    const windSample = {
      r: data[index] / 255,
      g: data[index + 1] / 255,
      b: data[index + 2] / 255,
      a: data[index + 3] / 255,
    };
    const { magnitude } = decodeWindVector(windSample);
    const speedRatio = getWindMagnitudeRatio(magnitude, windMagnitudeRange);
    const { r, g, b } = hexToRgb(getWindColor(magnitude, windMagnitudeRange));
    const midFlowEmphasis = 1 - Math.min(Math.abs(speedRatio - 0.34) * 2.1, 1);
    const weakFlowEmphasis = 1 - Math.min(speedRatio * 3.2, 1);
    const alpha = THREE.MathUtils.clamp(
      62 + (weakFlowEmphasis * 58) + (midFlowEmphasis * 64),
      58,
      164
    );

    data[index] = r;
    data[index + 1] = g;
    data[index + 2] = b;
    data[index + 3] = alpha;
  }

  context.putImageData(imageData, 0, 0);

  const diagnosticTexture = new THREE.CanvasTexture(canvas);
  diagnosticTexture.name = 'xr-flow-diagnostic-speed-texture';
  diagnosticTexture.colorSpace = THREE.SRGBColorSpace;
  diagnosticTexture.minFilter = THREE.LinearFilter;
  diagnosticTexture.magFilter = THREE.LinearFilter;
  diagnosticTexture.generateMipmaps = false;
  diagnosticTexture.wrapS = THREE.RepeatWrapping;
  diagnosticTexture.wrapT = THREE.ClampToEdgeWrapping;
  diagnosticTexture.needsUpdate = true;

  return diagnosticTexture;
}

function applyThumbstickNavigation(
  controller,
  delta,
  state,
  updateSpatialState,
  updatePlanetFromCurrentPose
) {
  const gamepad = controller.inputSource?.gamepad;
  if (!gamepad?.axes?.length) {
    return;
  }

  const axes = getPrimaryThumbstickAxes(gamepad.axes);
  const [horizontalAxis, verticalAxis] = axes;
  const nextState = {};
  let hasMovement = false;

  if (Math.abs(horizontalAxis) > XR_ROTATION_DEADZONE) {
    nextState.planetSideOffset = THREE.MathUtils.clamp(
      state.planetSideOffset + (horizontalAxis * delta * XR_THUMBSTICK_MOVE_SPEED),
      -1.4,
      1.4
    );
    hasMovement = true;
  }

  if (Math.abs(verticalAxis) > XR_ROTATION_DEADZONE) {
    nextState.planetDistance = THREE.MathUtils.clamp(
      state.planetDistance + (verticalAxis * delta * XR_THUMBSTICK_ZOOM_SPEED),
      XR_PLANET_MIN_DISTANCE,
      XR_PLANET_MAX_DISTANCE
    );
    hasMovement = true;
  }

  if (hasMovement) {
    updateSpatialState(nextState);
    updatePlanetFromCurrentPose();
  }
}

function getPrimaryThumbstickAxes(axes) {
  if (axes.length < 4) {
    return [axes[0] ?? 0, axes[1] ?? 0];
  }

  const firstPair = [axes[0] ?? 0, axes[1] ?? 0];
  const secondPair = [axes[2] ?? 0, axes[3] ?? 0];
  const firstMagnitude = Math.abs(firstPair[0]) + Math.abs(firstPair[1]);
  const secondMagnitude = Math.abs(secondPair[0]) + Math.abs(secondPair[1]);

  return secondMagnitude >= firstMagnitude ? secondPair : firstPair;
}

function getWindTracerQualityPreset() {
  if (typeof window === 'undefined') {
    return XR_WIND_TRACER_QUALITY_PRESETS.performance;
  }

  const params = new URLSearchParams(window.location.search);
  const requestedQuality = (params.get('xrWindQuality') ?? params.get('windQuality') ?? '').toLowerCase();

  return XR_WIND_TRACER_QUALITY_PRESETS[requestedQuality] ?? XR_WIND_TRACER_QUALITY_PRESETS.performance;
}

function getWindTracerQualityPresetByLabel(label) {
  return XR_WIND_TRACER_QUALITY_PRESETS[label] ?? XR_WIND_TRACER_QUALITY_PRESETS.performance;
}

function getNextWindTracerQualityPreset(currentLabel) {
  const currentIndex = XR_WIND_TRACER_QUALITY_ORDER.indexOf(currentLabel);
  const nextIndex = currentIndex < 0
    ? 0
    : (currentIndex + 1) % XR_WIND_TRACER_QUALITY_ORDER.length;

  return getWindTracerQualityPresetByLabel(XR_WIND_TRACER_QUALITY_ORDER[nextIndex]);
}

function getWindTracerQualityOrderIndex(label) {
  return Math.max(XR_WIND_TRACER_QUALITY_ORDER.indexOf(label), 0);
}

function formatWindTracerQualityKey(quality) {
  return `${quality.label}:${quality.count}:${quality.trailSegments}`;
}

function formatWindTracerQualityLabel(label) {
  if (label === 'performance') {
    return 'Low density';
  }

  if (label === 'balanced') {
    return 'Medium density';
  }

  return 'High density';
}

function formatWindTracerQualityShortLabel(label) {
  if (label === 'performance') {
    return 'Low';
  }

  if (label === 'balanced') {
    return 'Med';
  }

  return 'High';
}

function formatWindTracerQualityButtonLabel(label) {
  return `Density: ${formatWindTracerQualityShortLabel(label)}`;
}

function formatWindTracerQualityHoverLabel(label) {
  return `Tracer density: ${formatWindTracerQualityLabel(label)}`;
}

function formatWindTracerQualityStats(quality, layerCount = 1) {
  const layerQuality = getWindTracerLayerQuality(quality, layerCount);
  const totalTracers = layerQuality.count * Math.max(layerCount, 1);

  return `${totalTracers.toLocaleString()} tracers / ${layerQuality.trailSegments} trail steps`;
}

function formatStationTelemetry(telemetry = createInitialStationTelemetry()) {
  if (!Number.isFinite(telemetry.fps)) {
    return 'Station telemetry: calibrating';
  }

  const frameMs = Number.isFinite(telemetry.frameMs)
    ? `${telemetry.frameMs.toFixed(1)} ms`
    : '-- ms';
  const fallback = telemetry.autoFlowFallback ? ' / adaptive density' : '';

  return `Station telemetry: ${telemetry.fps} FPS / ${frameMs} / ${telemetry.status}${fallback}`;
}

function formatStationCoordinate(value, positiveSuffix, negativeSuffix) {
  if (!Number.isFinite(value)) {
    return '--';
  }

  const suffix = value >= 0 ? positiveSuffix : negativeSuffix;
  return `${Math.abs(value).toFixed(1)} deg ${suffix}`;
}

function formatStationSignedNumber(value, unit = '') {
  if (!Number.isFinite(value)) {
    return '--';
  }

  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}${unit ? ` ${unit}` : ''}`;
}

function createHud({ onAction }) {
  const group = new THREE.Group();
  const backplate = new THREE.Mesh(
    new THREE.PlaneGeometry(XR_HUD_BACKPLATE_WIDTH, XR_HUD_BACKPLATE_HEIGHT),
    new THREE.MeshBasicMaterial({
      color: 0x020812,
      transparent: true,
      opacity: 0.94,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    })
  );
  backplate.position.set(0, -0.06, -0.014);
  backplate.renderOrder = 998;
  group.add(backplate);

  const panel = createTexturedPlane(
    XR_HUD_PANEL_WIDTH,
    XR_HUD_PANEL_HEIGHT,
    createPanelTexture({ legendEntries: [] })
  );
  panel.position.set(0, XR_HUD_PANEL_Y, 0);
  panel.renderOrder = 999;
  group.add(panel);

  const legendPanel = createTexturedPlane(
    XR_HUD_LEGEND_WIDTH,
    XR_HUD_LEGEND_HEIGHT,
    createLegendPanelTexture({ legendEntries: [] })
  );
  legendPanel.position.set(0, XR_HUD_LEGEND_Y, 0.002);
  legendPanel.renderOrder = 999;
  group.add(legendPanel);

  const pointerLocal = new THREE.Vector3();
  const pointerCursor = new THREE.Mesh(
    new THREE.RingGeometry(0.018, 0.031, 32),
    new THREE.MeshBasicMaterial({
      color: 0xffd166,
      transparent: true,
      opacity: 0.96,
      side: THREE.DoubleSide,
      depthTest: false,
      depthWrite: false,
    })
  );
  pointerCursor.name = 'xr-hud-pointer-cursor';
  pointerCursor.position.z = 0.035;
  pointerCursor.renderOrder = 1304;
  pointerCursor.visible = false;
  group.add(pointerCursor);

  let hudState = {
    legendEntries: [],
    surfaceMode: 'constant',
    showWinds: true,
    stationTelemetry: createInitialStationTelemetry(),
    surfaceOpaque: true,
    windLayerBlendMode: DEFAULT_WIND_LAYER_BLEND_MODE,
    xrSurfaceStyle: XR_SURFACE_STYLES.darkGray,
    windTracerQuality: XR_INITIAL_WIND_TRACER_QUALITY.label,
  };
  let hoveredAction = null;

  const buttonDefs = [
    { label: 'Dark gray', action: 'surface-dark', x: -1.13, y: -0.16, width: 0.4 },
    { label: 'Data map', action: 'surface-data', x: -0.68, y: -0.16, width: 0.38 },
    { label: 'Pressure', action: 'surface-pressure', x: -0.25, y: -0.16, width: 0.38 },
    { label: 'Wind on', action: 'winds-on', x: 0.17, y: -0.16, width: 0.34 },
    { label: 'Wind off', action: 'winds-off', x: 0.56, y: -0.16, width: 0.36 },
    { label: 'Exit', action: 'exit', x: 1.01, y: -0.16, width: 0.3 },
    { label: 'Opaque on', action: 'opaque-on', x: -0.91, y: -0.36, width: 0.44 },
    { label: 'Opaque off', action: 'opaque-off', x: -0.4, y: -0.36, width: 0.46 },
    { label: 'Layer -', action: 'layer-down', x: 0.11, y: -0.36, width: 0.34 },
    { label: 'Layer +', action: 'layer-up', x: 0.5, y: -0.36, width: 0.34 },
    { label: 'Reset view', action: 'reset', x: 0.95, y: -0.36, width: 0.44 },
    {
      label: 'Density',
      hoverLabel: 'Cycle tracer density',
      action: 'wind-quality',
      x: -1.08,
      y: -0.52,
      width: 0.58,
    },
    {
      label: 'Layers',
      hoverLabel: 'Cycle layer combination',
      action: 'layer-blend',
      x: -0.43,
      y: -0.52,
      width: 0.56,
    },
  ];

  const buttons = buttonDefs.map(({ label, hoverLabel, action, x, y, width }) => {
    const displayLabel = getHudButtonDisplayLabel({ label, action }, hudState);
    const button = createTexturedPlane(width, XR_HUD_BUTTON_HEIGHT, createButtonTexture(displayLabel, { planeWidth: width }));
    button.position.set(x, y, 0.012);
    button.renderOrder = 1000;
    button.userData.xrAction = action;
    button.userData.xrLabel = label;
    button.userData.xrHoverLabel = hoverLabel ?? label;
    button.userData.xrWidth = width;
    button.userData.onAction = onAction;
    group.add(button);
    return button;
  });

  function refreshPanel() {
    replacePlaneTexture(
      panel,
      createPanelTexture({
        ...hudState,
        hoveredLabel: getHudButtonLabel(buttonDefs, hoveredAction, hudState),
      })
    );
  }

  function refreshLegend() {
    replacePlaneTexture(legendPanel, createLegendPanelTexture(hudState));
  }

  function refreshButtons() {
    const activeActions = getActiveHudActions(hudState);

    buttons.forEach((button) => {
      const label = getHudButtonDisplayLabel(button.userData, hudState);

      replacePlaneTexture(
        button,
        createButtonTexture(label, {
          isActive: activeActions.has(button.userData.xrAction),
          isHovered: button.userData.xrAction === hoveredAction,
          planeWidth: button.userData.xrWidth,
        })
      );
    });
  }

  return {
    group,
    interactiveObjects: buttons,
    dragObjects: [backplate, panel, legendPanel, ...buttons],
    update(state) {
      hudState = { ...hudState, ...state };
      refreshPanel();
      refreshLegend();
      refreshButtons();
    },
    setPointerTarget(action, worldPoint) {
      if (hoveredAction === action) {
        if (worldPoint) {
          pointerLocal.copy(worldPoint);
          group.worldToLocal(pointerLocal);
          pointerCursor.position.x = pointerLocal.x;
          pointerCursor.position.y = pointerLocal.y;
          pointerCursor.visible = true;
        } else {
          pointerCursor.visible = false;
        }
        return;
      }

      hoveredAction = action;
      if (worldPoint) {
        pointerLocal.copy(worldPoint);
        group.worldToLocal(pointerLocal);
        pointerCursor.position.x = pointerLocal.x;
        pointerCursor.position.y = pointerLocal.y;
        pointerCursor.visible = true;
      } else {
        pointerCursor.visible = false;
      }
      refreshPanel();
      refreshButtons();
    },
    dispose() {
      disposeObject(group);
    },
  };
}

function getHudButtonDisplayLabel(button, hudState) {
  if (button.action === 'wind-quality' || button.xrAction === 'wind-quality') {
    return formatWindTracerQualityButtonLabel(hudState.windTracerQuality);
  }

  if (button.action === 'layer-blend' || button.xrAction === 'layer-blend') {
    return formatWindLayerBlendButtonLabel(hudState.windLayerBlendMode);
  }

  return button.label ?? button.xrLabel ?? '';
}

function getHudButtonLabel(buttonDefs, action, hudState) {
  const button = buttonDefs.find((candidate) => candidate.action === action);

  if (button?.action === 'wind-quality') {
    return formatWindTracerQualityHoverLabel(hudState.windTracerQuality);
  }

  if (button?.action === 'layer-blend') {
    return `Layer combination: ${formatWindLayerBlendLabel(hudState.windLayerBlendMode)}`;
  }

  return button?.hoverLabel ?? button?.label ?? '';
}

function getActiveHudActions({
  surfaceMode,
  showWinds,
  surfaceOpaque = true,
  xrSurfaceStyle = XR_SURFACE_STYLES.darkGray,
} = {}) {
  const activeActions = new Set();

  if (xrSurfaceStyle === XR_SURFACE_STYLES.darkGray) {
    activeActions.add('surface-dark');
  } else if (surfaceMode === 'pressure') {
    activeActions.add('surface-pressure');
  } else {
    activeActions.add('surface-data');
  }

  activeActions.add(showWinds ? 'winds-on' : 'winds-off');
  activeActions.add(surfaceOpaque ? 'opaque-on' : 'opaque-off');
  activeActions.add('wind-quality');
  activeActions.add('layer-blend');

  return activeActions;
}

function createTexturedPlane(width, height, texture) {
  const geometry = new THREE.PlaneGeometry(width, height);
  const material = new THREE.MeshBasicMaterial({
    map: texture,
    transparent: true,
    side: THREE.DoubleSide,
    depthTest: false,
    depthWrite: false,
  });
  return new THREE.Mesh(geometry, material);
}

function createSceneLabelTexture(title, subtitle) {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 160;
  const context = canvas.getContext('2d');

  context.clearRect(0, 0, canvas.width, canvas.height);
  drawRoundedRect(context, 8, 8, 496, 144, 24, 'rgba(3, 9, 19, 0.76)', 'rgba(122, 212, 255, 0.38)');
  context.fillStyle = '#7ad4ff';
  context.font = '700 38px "Segoe UI", sans-serif';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(title, 256, 66);
  context.fillStyle = '#ecf4fb';
  context.font = '600 28px "Segoe UI", sans-serif';
  context.fillText(subtitle, 256, 110);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function replacePlaneTexture(mesh, texture) {
  mesh.material.map?.dispose();
  mesh.material.map = texture;
  mesh.material.needsUpdate = true;
}

function createPanelTexture({
  textureLevel,
  surfaceMode = 'constant',
  showWinds = true,
  selectedPoint,
  stationTelemetry = createInitialStationTelemetry(),
  surfaceOpaque = true,
  windLayerBlendMode = DEFAULT_WIND_LAYER_BLEND_MODE,
  xrSurfaceStyle = XR_SURFACE_STYLES.darkGray,
  windTracerQuality = XR_INITIAL_WIND_TRACER_QUALITY.label,
  hoveredLabel = '',
}) {
  const canvas = document.createElement('canvas');
  canvas.width = 1800;
  canvas.height = 640;
  const context = canvas.getContext('2d');
  const qualityPreset = getWindTracerQualityPresetByLabel(windTracerQuality);
  const windLayerCount = getWindLayerBlendCount(textureLevel, windLayerBlendMode);

  drawPanelBackground(context, canvas.width, canvas.height);

  context.fillStyle = '#7ad4ff';
  context.font = '700 38px "Segoe UI", sans-serif';
  context.fillText('Jupiter Climate Station', 76, 70);

  context.fillStyle = '#ecf4fb';
  context.font = '700 60px "Segoe UI", sans-serif';
  context.fillText('Virtual atmosphere lab', 76, 138);

  context.fillStyle = '#ecf4fb';
  context.font = '700 36px "Segoe UI", sans-serif';
  context.fillText(`Layer L${textureLevel ?? '--'}`, 76, 208);

  context.fillStyle = '#9bb3c7';
  context.font = '600 30px "Segoe UI", sans-serif';
  context.fillText(`Surface: ${formatSurfaceStatus(surfaceMode, xrSurfaceStyle)}`, 320, 208);

  context.fillStyle = '#9bb3c7';
  context.font = '600 28px "Segoe UI", sans-serif';
  context.fillText(`Winds: ${showWinds ? 'on' : 'off'}   Occlusion: ${surfaceOpaque ? 'opaque' : 'transparent'}`, 960, 208);

  context.fillStyle = '#9bb3c7';
  context.font = '600 27px "Segoe UI", sans-serif';
  context.fillText(
    `Tracer density: ${formatWindTracerQualityLabel(qualityPreset.label)} (${formatWindTracerQualityStats(qualityPreset, windLayerCount)})`,
    960,
    248
  );

  context.fillStyle = '#9bb3c7';
  context.font = '600 27px "Segoe UI", sans-serif';
  context.fillText(`Layer combination: ${formatWindLayerBlendLabel(windLayerBlendMode)}`, 960, 286);

  context.fillStyle = '#5ee7ff';
  context.font = '600 24px "Segoe UI", sans-serif';
  context.fillText('Research diagnostic: speed veil active', 960, 322);

  context.fillStyle = stationTelemetry.status === 'Low FPS' ? '#ffc659' : '#7ad4ff';
  context.font = '700 27px "Segoe UI", sans-serif';
  context.fillText(
    formatStationTelemetry(stationTelemetry),
    76,
    286
  );

  context.fillStyle = '#ecf4fb';
  context.font = '700 32px "Segoe UI", sans-serif';
  context.fillText('Active probe', 76, 348);

  context.fillStyle = selectedPoint?.colour ?? '#7ad4ff';
  context.fillRect(315, 321, 36, 36);
  context.fillStyle = '#9bb3c7';
  context.font = '600 28px "Segoe UI", sans-serif';

  if (selectedPoint) {
    context.fillText(`${selectedPoint.mag} m/s wind speed`, 380, 348);
    context.fillText(
      `Lat ${formatStationCoordinate(selectedPoint.latitude, 'N', 'S')}   Lon ${formatStationCoordinate(selectedPoint.longitude, 'E', 'W')}`,
      76,
      392
    );
    context.fillText(
      `Zonal ${formatStationSignedNumber(selectedPoint.zonalWind, 'm/s')}   Meridional ${formatStationSignedNumber(selectedPoint.meridionalWind, 'm/s')}`,
      76,
      432
    );
  } else {
    context.fillText('Point at the globe and press trigger to inspect flow', 380, 348);
    context.fillText('Probe: waiting for atmospheric sample', 76, 392);
  }

  context.fillStyle = '#7ad4ff';
  context.font = '700 28px "Segoe UI", sans-serif';
  context.fillText('Station controls', 76, 486);

  context.fillStyle = '#9bb3c7';
  context.font = '600 28px "Segoe UI", sans-serif';
  context.fillText(
    hoveredLabel ? `Aiming at: ${hoveredLabel}` : 'Aiming at: no button',
    72,
    568
  );

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function createLegendPanelTexture({ legendEntries = [] }) {
  const canvas = document.createElement('canvas');
  canvas.width = 1800;
  canvas.height = 340;
  const context = canvas.getContext('2d');

  drawPanelBackground(context, canvas.width, canvas.height);

  const activeEntries = legendEntries.slice(0, 2);
  if (!activeEntries.length) {
    context.fillStyle = '#9bb3c7';
    context.font = '600 34px "Segoe UI", sans-serif';
    context.fillText('Colour scale appears here when wind tracers or pressure surface are active.', 70, 180);
  }

  activeEntries.forEach((entry, index) => {
    drawLegendEntry(context, entry, 70, 92 + (index * 132), 1660);
  });

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function formatSurfaceStatus(surfaceMode, xrSurfaceStyle) {
  if (xrSurfaceStyle === XR_SURFACE_STYLES.darkGray) {
    return 'dark gray';
  }

  return surfaceMode === 'pressure' ? 'pressure map' : 'wind data map';
}

function createButtonTexture(label, {
  isActive = false,
  isHovered = false,
  planeWidth = 0.4,
  planeHeight = XR_HUD_BUTTON_HEIGHT,
} = {}) {
  const canvas = document.createElement('canvas');
  canvas.height = 160;
  canvas.width = Math.max(360, Math.round(canvas.height * (planeWidth / planeHeight)));
  const context = canvas.getContext('2d');
  const fillStyle = isHovered
    ? 'rgba(255,198,89,0.96)'
    : isActive
      ? 'rgba(26,118,150,0.94)'
      : 'rgba(20,72,102,0.88)';
  const strokeStyle = isHovered
    ? 'rgba(255,246,205,0.98)'
    : isActive
      ? 'rgba(122,212,255,0.96)'
      : 'rgba(174,226,255,0.72)';
  const textColor = isHovered ? '#06101a' : '#ecf4fb';

  drawRoundedRect(context, 6, 6, canvas.width - 12, canvas.height - 12, 30, fillStyle, strokeStyle);
  context.fillStyle = textColor;
  setFittedCanvasFont(context, label, {
    maxWidth: canvas.width - 64,
    maxFontSize: 46,
    minFontSize: 29,
    weight: 700,
  });
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(label, canvas.width / 2, canvas.height / 2 + 3);

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function setFittedCanvasFont(context, text, {
  maxWidth,
  maxFontSize,
  minFontSize,
  weight = 700,
  family = '"Segoe UI", sans-serif',
}) {
  for (let size = maxFontSize; size >= minFontSize; size -= 1) {
    context.font = `${weight} ${size}px ${family}`;

    if (context.measureText(text).width <= maxWidth) {
      return;
    }
  }

  context.font = `${weight} ${minFontSize}px ${family}`;
}

function drawPanelBackground(context, width, height) {
  const gradient = context.createLinearGradient(0, 0, 0, height);
  gradient.addColorStop(0, 'rgba(8,24,38,0.98)');
  gradient.addColorStop(1, 'rgba(2,8,18,0.99)');
  drawRoundedRect(context, 8, 8, width - 16, height - 16, 36, gradient, 'rgba(174,226,255,0.58)');
}

function drawLegendEntry(context, entry, x, y, width) {
  context.fillStyle = '#ecf4fb';
  context.font = '700 24px "Segoe UI", sans-serif';
  context.fillText(entry.title, x, y - 20);

  context.fillStyle = '#7ad4ff';
  context.font = '24px "Segoe UI", sans-serif';
  context.textAlign = 'right';
  context.fillText(entry.subtitle, x + width, y - 20);
  context.textAlign = 'left';

  const gradient = context.createLinearGradient(x, y, x + width, y);
  entry.stops.forEach((stop) => {
    gradient.addColorStop(stop.position, stop.color);
  });

  drawRoundedRect(context, x, y, width, 34, 17, gradient, 'rgba(255,255,255,0.24)');

  context.fillStyle = '#ecf4fb';
  context.font = '22px "Segoe UI", sans-serif';
  entry.stops.forEach((stop) => {
    const labelX = x + (stop.position * width);
    context.textAlign = stop.position === 0 ? 'left' : stop.position === 1 ? 'right' : 'center';
    context.fillText(stop.label, labelX, y + 70);
  });
  context.textAlign = 'left';
}

function drawRoundedRect(context, x, y, width, height, radius, fillStyle, strokeStyle) {
  context.beginPath();
  context.moveTo(x + radius, y);
  context.arcTo(x + width, y, x + width, y + height, radius);
  context.arcTo(x + width, y + height, x, y + height, radius);
  context.arcTo(x, y + height, x, y, radius);
  context.arcTo(x, y, x + width, y, radius);
  context.closePath();
  context.fillStyle = fillStyle;
  context.fill();

  if (strokeStyle) {
    context.strokeStyle = strokeStyle;
    context.lineWidth = 2;
    context.stroke();
  }
}

function disposeObject(object, { disposeMaps = true } = {}) {
  object.traverse((child) => {
    child.geometry?.dispose();

    if (Array.isArray(child.material)) {
      child.material.forEach((material) => {
        if (disposeMaps) {
          material.map?.dispose();
        }
        material.dispose();
      });
      return;
    }

    if (disposeMaps) {
      child.material?.map?.dispose();
    }
    child.material?.dispose();
  });
}
